import { startAndPollAccountDeletion } from "./accountDeletionPolling";
import { axiosInstance } from "./axios";
import { unwrapData, unwrapEntity } from "./apiResult";
import { withRosterUserNames, type SessionTeamRosterPlayer } from "./rosterAutoTag";
import { auth, firestore } from "./firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  normalizeCommunityGroupIdentifiers,
  normalizeAdminSessionIdentifiers,
  normalizeSessionIdentifier,
  normalizeOptionalUserIdentifier,
  normalizeUserIdentifier,
} from "./identifiers";
import {
  SessionStatus,
  SessionMode,
  SessionType,
  RecordingSetup,
  PointSystem,
  CourtType,
  SubscriptionType,
  UserSegmentType,
} from "./constants";
import type { TourId } from "../components/tour/types";
import type { AccountDataStatus, AccountDataVerification } from "./types";
import type {
  ExtraHoursConfig,
  ExtraHoursPurchaseConfig,
  ProPricing,
  RegionalPrice,
  RegionalPricingConfig,
  ExtraHoursCheckoutSession,
  GroupHourCredits,
  HourCreditFilters,
  HourCreditPage,
  FBUser,
  UpdateSessionOwnerRequest,
  UpdateSessionStatusRequest,
  UpdateSessionVideoUrlRequest,
  UpdateVideoSourceRequest,
  UpdateSessionVideoFpsRequest,
  UpdateSessionModeRequest,
  UpdateSessionTypeRequest,
  UpdateSessionRecordingSetupRequest,
  UpdateSessionPointSystemRequest,
  UpdateSessionCourtTypeRequest,
  UpdateSessionTeamsRequest,
  CreateSessionRequest,
  ShareSessionRequest,
  AddSessionTitleRequest,
  CreateSessionPlayerNameRequest,
  AddSessionEditorRequest,
  User,
  Session,
  SessionCard,
  EditShotRequest,
  BatchIgnoreShotsRequest,
  BatchIgnoreShotsResponse,
  EditTagRequest,
  UpdateSessionVerifiedPercentRequest,
  EditHighlightRequest,
  EditClusterTagRequest,
  CreateTagRequest,
  CreateShotRequest,
  CreateClusterTagRequest,
  FBShotV4,
  FBTagV4,
  FBHighlightV4,
  FBClusterTagV4,
  FBPlayerName,
  FBMixtape,
  TimelinePoint,
  SummaryStats,
  AdminApiStats,
  UserStats,
  SessionMonthlyUsage,
  TagShotHighlight,
  OverrideSessionHoopRequest,
  OverrideSessionCourtRequest,
  FBKeyframeV4,
  BoxAnnotation,
  CourtAnnotation,
  FBQueueJob,
  DescriptionChangeRequest,
  FBAdminProtectedWord,
  Group,
  ApiKey,
  ApiRequestLog,
  MintedApiKey,
  CreateWebhookEndpointRequest,
  CreatedWebhookEndpoint,
  Organization,
  OrganizationApiCredits,
  OrganizationApiDashboard,
  OrganizationApiSummary,
  OrganizationCreditsCheckout,
  RedeemPricingCodeResponse,
  ApiPricingCode,
  WebhookDelivery,
  WebhookEndpoint,
  FBTeamSeason,
  Competition,
  FBTeamPlayer,
  AddCompetitionTeamsRequest,
  CreateTeamRequest,
  BulkCreateTeamPlayersRequest,
  CreateTeamPlayerRequest,
  EditTeamPlayerRequest,
  GroupRosterPlayer,
  RemoveTeamPlayerRequest,
  DeleteGroupRequest,
  DemoteGroupMemberRequest,
  EditGroupBasicRequest,
  EditGroupDetailsRequest,
  GroupMember,
  PromoteGroupMemberRequest,
  RemoveGroupMemberRequest,
  EditGroupLogoRequest,
  CreateGroupInvitationRequest,
  GroupStatsBoxScore,
  TeamPlayerSessionLog,
  GiveUserFreeTrialRequest,
  UpdateUserSubscriptionTypeRequest,
  UpdateUserSegmentTypeRequest,
  UpdateUserTypeRequest,
  InitiateUploadRequest,
  InitiateUploadResponse,
  PaidInvoiceItem,
  AdminInvoiceItem,
  GroupInvoice,
  CommunityPlayerStatsBoard,
  AdminInvoicePageResponse,
  AdminInvoiceFilters,
  AdminInvoiceRefundResponse,
  ReferralsRankingResponse,
  ReferralHomeStats,
  CreateDownloadResponse,
  CreateUserRequest,
  CreateOrganizationRequest,
  UpdateOrganizationApiTermsRequest,
  FeatureAnnouncement,
  FeatureAnnouncementListResponse,
  FeatureAnnouncementPayload,
  MaintenanceBlockerSettings,
  PublicSessionStats,
  StatSheetLanguage,
  StatSheetRevision,
  StatSheetPreview,
  StatSheetPayload,
  ConfirmedStatSheet,
  ConfirmStatSheetRequest,
  ParentPermissionStatus,
  ReferralValidation,
  PublicUserBrief,
  ReferralMeta,
  PublicTeamCard,
  GuestInvitePreview,
} from "./types";

// === Announcement Endpoints ===

// Built on use, not at import: a module-level doc() runs whenever anything
// imports services, including tests that mock ./firebase without firestore.
const maintenanceBlockerRef = () =>
  doc(firestore, "Settings", "mobileMaintenanceBlocker");

export const fetchMaintenanceBlockerSettings =
  async (): Promise<MaintenanceBlockerSettings> => {
    const snapshot = await getDoc(maintenanceBlockerRef());
    if (!snapshot.exists()) {
      return { enabled: false };
    }

    const data = snapshot.data() as Partial<MaintenanceBlockerSettings>;
    return {
      enabled: data.enabled === true,
      returnAt: typeof data.returnAt === "string" ? data.returnAt : null,
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy ?? null,
    };
  };

export const updateMaintenanceBlockerSettings = async (
  enabled: boolean,
  returnAt?: string | null,
): Promise<MaintenanceBlockerSettings> => {
  const payload: MaintenanceBlockerSettings = {
    enabled,
    returnAt: enabled ? returnAt ?? null : null,
    updatedBy: auth.currentUser?.uid ?? null,
  };

  await setDoc(
    maintenanceBlockerRef(),
    {
      ...payload,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return payload;
};

export const fetchAdminAnnouncements =
  async (): Promise<FeatureAnnouncementListResponse> => {
    return axiosInstance
      .get("/admin/announcements", { params: { include_inactive: true } })
      .then((res) => res.data);
  };

export const createAdminAnnouncement = async (
  payload: FeatureAnnouncementPayload,
): Promise<FeatureAnnouncement> => {
  return axiosInstance
    .post("/admin/announcements", payload)
    .then((res) => res.data);
};

export const updateAdminAnnouncement = async (
  announcementId: string,
  payload: Partial<FeatureAnnouncementPayload>,
): Promise<FeatureAnnouncement> => {
  const { id: _id, ...updatePayload } = payload;
  return axiosInstance
    .patch(`/admin/announcements/${announcementId}`, updatePayload)
    .then((res) => res.data);
};

// === Session Endpoints ===

/**
 * Deletes a session
 * @param id (string): ID of the session to delete
 */
export const deleteSession = async (id: string): Promise<boolean> => {
  return axiosInstance
    .delete(`/admin/sessions/${id}`)
    .then((res) => res.data);
};

export const deleteUserSession = async (
  sessionId: string,
  userId: string,
  deleteAll: boolean,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/${sessionId}/delete`, { userId, deleteAll })
    .then((res) => res.data);
};

export const restoreSession = async (id: string): Promise<boolean> => {
  return axiosInstance
    .post(`/admin/sessions/${id}/restore`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `restoreSession`", err));
};

// Every workflow runs on Flash; the queue's legacy action/clip engine fields
// belonged to the retired beta pipeline.
export interface InferenceOptions {
  workflow: string;
}

export const submitSessionInference = async (
  sessionId: string,
  options: InferenceOptions,
) => {
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/infer/queue`, { workflow: options.workflow })
    .then((res) => res.data);
};

export const fetchLatestAutoMixtapes = async (
  userId: string,
  page: number = 0,
  limit: number = 10,
): Promise<FBMixtape[]> => {
  return axiosInstance
    .get(`/mixtapes/user/${userId}/latest`, { params: { page, limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchLatestAutoMixtapes`:", err);
      return [];
    });
};

export const fetchCustomMixtapes = async (
  userId: string,
  page: number = 0,
  limit: number = 50,
): Promise<FBMixtape[]> => {
  return axiosInstance
    .get(`/mixtapes/user/${userId}/custom`, { params: { page, limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCustomMixtapes`:", err);
      return [];
    });
};

export const regenerateSessionKeyframes = async (sessionId: string) => {
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/keyframes/regenerate`)
    .then((res) => res.data);
};

export type JerseyRescueMode = "dry-run" | "apply" | "undo";

/** plan.json written by the jersey-rescue job's dry run (jobs/jersey_rescue/plan.py). */
export type JerseyRescuePlan = {
  skip_reason?: string | null;
  new_clusters?: Array<{ class: number; jersey: number | null; team_id: number | null; tags: number }>;
  merges?: Array<{ into: string; jersey: number; tags: number; reason: string }>;
  tags_repointed?: number;
  tags_left_in_pipeline_minus1?: number;
  tags_kept_where_coach_put_them?: number;
  notes?: string[];
};

export type JerseyRescueStatus = {
  runVersion: number;
  hasJerseys: boolean;
  plan?: JerseyRescuePlan | null;
  plannedAt?: number | null;
  status?: "applied" | "undone" | null;
  appliedAt?: number | null;
  newClusters: number;
  tagsRepointed: number;
};

export const fetchJerseyRescueStatus = async (sessionId: string): Promise<JerseyRescueStatus> => {
  return axiosInstance
    .get(`/admin/sessions/${sessionId}/jersey-rescue`)
    .then((res) => res.data);
};

export const runJerseyRescue = async (sessionId: string, mode: JerseyRescueMode): Promise<string> => {
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/jersey-rescue`, { mode })
    .then((res) => res.data);
};

// === Community Endpoints ===

/**
 * A list of teams/groups with ids normalized. A row the API sent without an
 * id or owner is dropped (and logged) rather than failing the whole list --
 * one malformed team must not empty every picker that lists the rest.
 */
export const normalizeCommunityGroups = (groups: Group[] | null | undefined): Group[] =>
  (groups || []).flatMap((group) => {
    try {
      return [normalizeCommunityGroupIdentifiers(group)];
    } catch (err) {
      console.warn("Skipping malformed team/group row", group, err);
      return [];
    }
  });

const normalizeTeamPlayers = (
  players: FBTeamPlayer[] | null | undefined,
): FBTeamPlayer[] =>
  (players || []).map((player) => {
    const raw = player as FBTeamPlayer & Record<string, any>;
    return {
      ...player,
      firstName: raw.firstName ?? raw.first_name,
      lastName: raw.lastName ?? raw.last_name,
      profilePicture: raw.profilePicture ?? raw.profile_picture,
      position:
        raw.position ?? raw.playerPosition ?? raw.player_position ?? undefined,
      name:
        raw.name ??
        [raw.firstName ?? raw.first_name, raw.lastName ?? raw.last_name]
          .filter(Boolean)
          .join(" "),
    };
  });

export const fetchCommunityGroups = async (
  page: number = 0,
  limit: number = 50,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/groups`, { params: { page, limit } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchCommunityGroups`:", err);
      return [];
    });
};

export const fetchDayGroups = async (
  year: number,
  month: number,
  day: number,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/groups/day`, { params: { year, month, day } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchDayGroups`:", err);
      return [];
    });
};

export const fetchDayTeams = async (
  year: number,
  month: number,
  day: number,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/teams/day`, { params: { year, month, day } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchDayTeams`:", err);
      return [];
    });
};

export const fetchDayCompetitions = async (
  year: number,
  month: number,
  day: number,
): Promise<Competition[]> => {
  return axiosInstance
    .get(`/admin/community/competitions/day`, { params: { year, month, day } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchDayCompetitions`:", err);
      return [];
    });
};

export const fetchMonthGroups = async (
  year: number,
  month: number,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/groups/month`, { params: { year, month } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchMonthGroups`:", err);
      return [];
    });
};

export const fetchMonthTeams = async (
  year: number,
  month: number,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/teams/month`, { params: { year, month } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchMonthTeams`:", err);
      return [];
    });
};

export const fetchMonthCompetitions = async (
  year: number,
  month: number,
): Promise<Competition[]> => {
  return axiosInstance
    .get(`/admin/community/competitions/month`, { params: { year, month } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchMonthCompetitions`:", err);
      return [];
    });
};

export const fetchCommunityGroup = async (
  groupId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}`)
    .then((res) => normalizeCommunityGroupIdentifiers(res.data))
    .catch(async (err) => {
      console.error("Error in `fetchCommunityGroup`:", err);
      const groups = await fetchCommunityGroups(0, 1000);
      return groups.find((group) => group.id === groupId);
    });
};

/**
 * Direct group lookup with no list-scan fallback. For probe paths (e.g.
 * global search "is this ID a group?") where a miss is the common case —
 * `fetchCommunityGroup`'s fallback fetches 1000 groups on every miss.
 */
export const fetchCommunityGroupDirect = async (
  groupId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}`)
    .then((res) => normalizeCommunityGroupIdentifiers(res.data))
    .catch(() => undefined);
};

export const fetchCommunityGroupIdByShareCode = async (
  shareCode: string,
): Promise<string | undefined> => {
  const code = shareCode.trim();
  if (!code) return undefined;
  return axiosInstance
    .get(`/admin/groups/share/${encodeURIComponent(code)}`)
    .then((res) => String(normalizeCommunityGroupIdentifiers(res.data).id))
    .catch(() => undefined);
};

export const fetchUserCommunityGroup = async (
  groupId: string,
  userId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .get(`/groups/${groupId}`, { params: { userId } })
    .then((res) => normalizeCommunityGroupIdentifiers(res.data))
    .catch((err) => {
      console.error("Error in `fetchUserCommunityGroup`:", err);
      throw err;
    });
};

export const fetchUserCommunityGroups = async (
  userId: string,
  page: number = 0,
  limit: number = 50,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/hub/community/user/${userId}/groups`, { params: { page, limit } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchUserCommunityGroups`:", err);
      return [];
    });
};

export const fetchOrganizations = async (
  page: number = 0,
  limit: number = 50,
): Promise<Organization[]> => {
  return axiosInstance
    .get(`/admin/organizations`, { params: { page, limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchOrganizations`:", err);
      return [];
    });
};

export const fetchOrganization = async (
  organizationId: string,
): Promise<Organization | undefined> => {
  return axiosInstance
    .get(`/admin/organizations/${organizationId}`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchOrganization`:", err);
      return undefined;
    });
};

export const fetchOrganizationGroups = async (
  organizationId: string,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/organizations/${organizationId}/groups`)
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchOrganizationGroups`:", err);
      return [];
    });
};

export const fetchOrganizationApiSummary = async (
  organizationId: string,
): Promise<OrganizationApiSummary | undefined> => {
  return axiosInstance
    .get(`/admin/organizations/${organizationId}/api-summary`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchOrganizationApiSummary`:", err);
      return undefined;
    });
};

export const fetchOrganizationApiCredits = async (
  organizationId: string,
): Promise<OrganizationApiCredits | undefined> => {
  return axiosInstance
    .get(`/admin/organizations/${organizationId}/credits`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchOrganizationApiCredits`:", err);
      return undefined;
    });
};

/** Positive `cents` grants credits at no charge; negative takes them back. */
export const grantOrganizationApiCredits = async (
  organizationId: string,
  body: { cents: number; note?: string },
): Promise<OrganizationApiCredits> => {
  return axiosInstance
    .post(`/admin/organizations/${organizationId}/credits/grant`, body)
    .then((res) => res.data);
};

export const createOrganization = async (
  body: CreateOrganizationRequest,
): Promise<Organization> => {
  return axiosInstance
    .post(`/admin/organizations`, body)
    .then((res) => res.data);
};

export const updateOrganizationApiTerms = async (
  organizationId: string,
  body: UpdateOrganizationApiTermsRequest,
): Promise<Organization> => {
  return axiosInstance
    .put(`/admin/organizations/${organizationId}/api-terms`, body)
    .then((res) => res.data);
};

export const updateOrganizationFeature = async (
  organizationId: string,
  body: {
    name: string;
    enabled: boolean;
    apiMinimumHoursPerMonth?: number;
    apiPricePerHourUsd?: number;
  },
): Promise<Organization> => {
  return axiosInstance
    .put(`/admin/organizations/${organizationId}/features`, body)
    .then((res) => res.data);
};

export const fetchMyOrganization = async (): Promise<Organization | undefined> => {
  return axiosInstance
    .get(`/organization/me`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchMyOrganization`:", err);
      return undefined;
    });
};

/**
 * The caller's developer account (an API organization they own), `null` when
 * they have none. Throws on any other failure so callers can tell "none" from
 * "unknown".
 */
export const fetchMyDeveloperAccount = async (): Promise<Organization | null> => {
  try {
    const res = await axiosInstance.get(`/organization/me`);
    const org: Organization | undefined = res.data;
    // The server folds the legacy `organizationType: "api"` into `features`.
    return org && (org.features ?? []).includes("api") ? org : null;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

/** Creates the caller's developer account. Without a name the server names
 * it after the user. */
export const createDeveloperAccount = async (
  name?: string,
): Promise<Organization> => {
  return axiosInstance
    .post(`/organization/developer`, name ? { name } : {})
    .then((res) => res.data);
};

export const fetchMyOrganizationGroups = async (): Promise<Group[]> => {
  return axiosInstance
    .get(`/organization/me/groups`)
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchMyOrganizationGroups`:", err);
      return [];
    });
};

export const fetchMyOrganizationApiDashboard =
  async (): Promise<OrganizationApiDashboard | undefined> => {
    return axiosInstance
      .get(`/organization/me/api-dashboard`)
      .then((res) => res.data)
      .catch((err) => {
        console.error("Error in `fetchMyOrganizationApiDashboard`:", err);
        return undefined;
      });
  };

export const createMyOrganizationCreditsCheckout = async (body?: {
  successUrl?: string;
  cancelUrl?: string;
  /** A dollar top-up, in cents. Or `hours` of video at the org's rate. */
  amountCents?: number;
  hours?: number;
}): Promise<OrganizationCreditsCheckout> => {
  return axiosInstance
    .post(`/organization/me/credits/checkout`, body || {})
    .then((res) => res.data);
};

/** Move the caller's API organization to a pricing code's rate. */
export const redeemMyPricingCode = async (
  code: string,
): Promise<RedeemPricingCodeResponse> =>
  axiosInstance
    .post(`/organization/me/pricing-code`, { code })
    .then((res) => res.data);

export const fetchMyApiRequests = async (params?: {
  limit?: number;
  beforeId?: number;
  status?: "succeeded" | "failed";
}): Promise<ApiRequestLog[]> => {
  return axiosInstance
    .get(`/organization/me/api-requests`, { params })
    .then((res) => res.data);
};

export const fetchMyOrganizationApiKeys = async (): Promise<ApiKey[]> => {
  return axiosInstance
    .get(`/organization/me/api-keys`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchMyOrganizationApiKeys`:", err);
      return [];
    });
};

export const createMyOrganizationApiKey = async (
  name: string,
): Promise<MintedApiKey> => {
  return axiosInstance
    .post(`/organization/me/api-keys`, { name })
    .then((res) => res.data);
};

export const revokeMyOrganizationApiKey = async (id: number): Promise<void> => {
  return axiosInstance.delete(`/organization/me/api-keys/${id}`).then(() => undefined);
};

export const fetchMyOrganizationWebhookEndpoints =
  async (): Promise<WebhookEndpoint[]> => {
    return axiosInstance
      .get(`/organization/me/webhook-endpoints`)
      .then((res) => res.data)
      .catch((err) => {
        console.error("Error in `fetchMyOrganizationWebhookEndpoints`:", err);
        return [];
      });
  };

export const createMyOrganizationWebhookEndpoint = async (
  body: CreateWebhookEndpointRequest,
): Promise<CreatedWebhookEndpoint> => {
  return axiosInstance
    .post(`/organization/me/webhook-endpoints`, body)
    .then((res) => res.data);
};

export const fetchMyOrganizationWebhookDeliveries = async (
  params?: { endpointId?: number; limit?: number },
): Promise<WebhookDelivery[]> => {
  return axiosInstance
    .get(`/organization/me/webhook-deliveries`, { params })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchMyOrganizationWebhookDeliveries`:", err);
      return [];
    });
};

export const fetchCommunityTeams = async (
  page: number = 0,
  limit: number = 50,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/teams`, { params: { page, limit } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchCommunityTeams`:", err);
      return [];
    });
};

export const fetchCommunityTeam = async (
  teamId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .get(`/hub/community/teams/${teamId}`)
    .then((res) => normalizeCommunityGroupIdentifiers(res.data))
    .catch(async (err) => {
      console.error("Error in `fetchCommunityTeam`:", err);
      const teams = await fetchCommunityTeams(0, 1000);
      return teams.find((team) => team.id === teamId);
    });
};

export const fetchUserCommunityTeams = async (
  userId: string,
  page: number = 0,
  limit: number = 50,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/hub/community/user/${userId}/teams`, { params: { page, limit } })
    .then((res) => normalizeCommunityGroups(res.data))
    .catch((err) => {
      console.error("Error in `fetchUserCommunityTeams`:", err);
      return [];
    });
};

export const fetchCommunityCompetitions = async (
  page: number = 0,
  limit: number = 50,
): Promise<Competition[]> => {
  return axiosInstance
    .get(`/admin/community/competitions`, { params: { page, limit } })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchCommunityCompetitions`:", err),
    );
};

export const fetchCommunityCompetition = async (
  competitionId: string,
): Promise<Competition | undefined> => {
  return axiosInstance
    .get(`/hub/community/competitions/${competitionId}`)
    .then((res) => res.data)
    .catch(async (err) => {
      console.error("Error in `fetchCommunityCompetition`:", err);
      const competitions = await fetchCommunityCompetitions(0, 1000);
      return competitions.find((competition) => competition.id === competitionId);
    });
};

export const fetchUserCommunityCompetitions = async (
  userId: string,
  page: number = 0,
  limit: number = 50,
): Promise<Competition[]> => {
  return axiosInstance
    .get(`/hub/community/user/${userId}/competitions`, {
      params: { page, limit },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchUserCommunityCompetitions`:", err),
    );
};

export const fetchCommunityGroupMembers = async (
  groupId: string,
  page: number = 0,
  limit: number = 200,
): Promise<User[]> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}/members`, {
      params: { page, limit },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchCommunityGroupMembers`:", err),
    );
};

/**
 * A group's members, each annotated with `isGroupOwner` and `groupRole`. The
 * owner is on page 0. Throws, so a screen can say the list failed to load
 * rather than showing an empty team.
 */
export const fetchGroupMembersOrThrow = async (
  groupId: string,
  page: number = 0,
  limit: number = 200,
): Promise<GroupMember[]> =>
  axiosInstance
    .get(`/groups/members/${groupId}`, { params: { page, limit } })
    .then((res) => res.data);

export const fetchUserCommunityGroupMembers = async (
  groupId: string,
  page: number = 0,
  limit: number = 200,
): Promise<User[]> => {
  return fetchGroupMembersOrThrow(groupId, page, limit).catch((err) =>
    console.error("Error in `fetchUserCommunityGroupMembers`:", err),
  ) as Promise<User[]>;
};

export const promoteGroupMemberOrThrow = async (body: PromoteGroupMemberRequest): Promise<boolean> =>
  axiosInstance.post(`/groups/members/promote/admin`, body).then((res) => res.data);

export const demoteGroupMemberOrThrow = async (body: DemoteGroupMemberRequest): Promise<boolean> =>
  axiosInstance.post(`/groups/members/demote/admin`, body).then((res) => res.data);

/** Removes a member; `removerId === memberId` is the member leaving. */
export const removeGroupMemberOrThrow = async (body: RemoveGroupMemberRequest): Promise<boolean> =>
  axiosInstance.post(`/groups/members/remove`, body).then((res) => res.data);

/**
 * Owner only: hands the team to another user, who must already be on it.
 * The previous owner stays on as an admin. Same endpoint mobile uses.
 */
export const transferGroupOwnershipOrThrow = async (body: {
  currentOwnerId: string;
  newOwnerId: string;
  groupId: string;
}): Promise<boolean> => axiosInstance.post(`/groups/owner/change`, body).then((res) => res.data);

/** Owner only. The server cancels the group's Pro subscription before deleting. */
export const deleteGroupOrThrow = async (body: DeleteGroupRequest): Promise<boolean> =>
  axiosInstance.post(`/groups/delete`, body).then((res) => res.data);

export const removeCommunityGroupMember = async (
  groupId: string,
  memberId: string,
  removerId: string,
): Promise<boolean> => {
  return removeGroupMemberOrThrow({ removerId, memberId, groupId }).catch((err) => {
    console.error("Error in `removeCommunityGroupMember`:", err);
    throw err;
  });
};

export const promoteCommunityGroupMemberToAdmin = async (
  groupId: string,
  memberId: string,
  promoterId: string,
): Promise<boolean> => {
  return promoteGroupMemberOrThrow({ promoterId, memberId, groupId }).catch((err) => {
    console.error("Error in `promoteCommunityGroupMemberToAdmin`:", err);
    throw err;
  });
};

export const fetchCommunityGroupTeams = async (
  groupId: string,
  page: number = 0,
  limit: number = 100,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}/teams`, {
      params: { page, limit },
    })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchCommunityGroupTeams`:", err));
};

export const fetchCommunityGroupInvoices = async (
  groupId: string,
  limit: number = 50,
): Promise<GroupInvoice[]> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}/invoices`, { params: { limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityGroupInvoices`:", err);
      return [];
    });
};

export const fetchUserCommunityGroupInvoices = async (
  groupId: string,
  limit: number = 50,
): Promise<GroupInvoice[]> => {
  return axiosInstance
    .get(`/groups/${groupId}/invoices`, { params: { limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchUserCommunityGroupInvoices`:", err);
      return [];
    });
};

export const disableCommunityGroupSubscription = async (
  groupId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/groups/${groupId}/subscription/disable`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `disableCommunityGroupSubscription`:", err);
      throw err;
    });
};

export const grantCommunityGroupProSubscription = async (
  groupId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/groups/${groupId}/subscription/pro`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `grantCommunityGroupProSubscription`:", err);
      throw err;
    });
};

export const giveCommunityGroupFreeTrial = async (
  groupId: string,
  options?: GiveUserFreeTrialRequest,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/groups/${groupId}/subscription/free-trial`, options)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `giveCommunityGroupFreeTrial`:", err);
      throw err;
    });
};

export const disableUserCommunityGroupSubscription = async (
  groupId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/groups/${groupId}/subscription/disable`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `disableUserCommunityGroupSubscription`:", err);
      throw err;
    });
};

export const fetchCommunityGroupMemberStats = async (
  groupId: string,
  userId: string,
): Promise<CommunityPlayerStatsBoard | undefined> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}/members/${userId}/stats`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityGroupMemberStats`:", err);
      throw err;
    });
};

export const fetchUserCommunityGroupMemberStats = async (
  groupId: string,
  userId: string,
): Promise<CommunityPlayerStatsBoard | undefined> => {
  return axiosInstance
    .get(`/groups/${groupId}/members/${userId}/stats`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchUserCommunityGroupMemberStats`:", err);
      throw err;
    });
};

export const fetchCommunityGroupSessionsCount = async (
  groupId: string,
): Promise<number> => {
  return axiosInstance
    .get(`/admin/community/groups/${groupId}/sessions/count`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityGroupSessionsCount`:", err);
      return 0;
    });
};

export const fetchCommunityTeamSessionsCount = async (
  teamId: string,
): Promise<number> => {
  return axiosInstance
    .get(`/admin/community/teams/${teamId}/sessions/count`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityTeamSessionsCount`:", err);
      return 0;
    });
};

export const fetchCommunityTeamInvoices = async (
  teamId: string,
  limit: number = 50,
): Promise<GroupInvoice[]> => {
  return axiosInstance
    .get(`/hub/community/teams/${teamId}/invoices`, { params: { limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityTeamInvoices`:", err);
      return [];
    });
};

export const disableCommunityTeamSubscription = async (
  teamId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/teams/${teamId}/subscription/disable`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `disableCommunityTeamSubscription`:", err);
      throw err;
    });
};

export const grantCommunityTeamProSubscription = async (
  teamId: string,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/teams/${teamId}/subscription/pro`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `grantCommunityTeamProSubscription`:", err);
      throw err;
    });
};

export const giveCommunityTeamFreeTrial = async (
  teamId: string,
  options?: GiveUserFreeTrialRequest,
): Promise<Group | undefined> => {
  return axiosInstance
    .post(`/admin/community/teams/${teamId}/subscription/free-trial`, options)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `giveCommunityTeamFreeTrial`:", err);
      throw err;
    });
};

export const fetchCommunityTeamPlayerStats = async (
  teamId: string,
  playerId: string,
): Promise<CommunityPlayerStatsBoard | undefined> => {
  return axiosInstance
    .get(`/hub/community/teams/${teamId}/players/${playerId}/stats`)
    .then((res) => res.data)
    .catch(() => undefined);
};

export const fetchUserCommunityTeamPlayerStats = async (
  teamId: string,
  playerId: string,
): Promise<CommunityPlayerStatsBoard | undefined> => {
  return axiosInstance
    .get(`/teams/${teamId}/players/${playerId}/stats`)
    .then((res) => res.data)
    .catch(() => undefined);
};

export const fetchCommunityGroupSessions = async (
  groupId: string,
  page: number = 0,
  limit: number = 100,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/hub/community/groups/${groupId}/sessions`, {
      params: { page, limit },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) =>
      console.error("Error in `fetchCommunityGroupSessions`:", err),
    );
};

export const fetchCommunityTeamMembers = async (
  teamId: string,
  page: number = 0,
  limit: number = 200,
): Promise<FBTeamPlayer[]> => {
  return axiosInstance
    .get(`/hub/community/teams/${teamId}/members`, {
      params: { page, limit },
    })
    .then((res) => normalizeTeamPlayers(res.data))
    .catch((err) => {
      console.error("Error in `fetchCommunityTeamMembers`:", err);
      return [];
    });
};

export const fetchCommunityTeamSessions = async (
  teamId: string,
  page: number = 0,
  limit: number = 100,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/hub/community/teams/${teamId}/sessions`, {
      params: { page, limit },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) =>
      console.error("Error in `fetchCommunityTeamSessions`:", err),
    );
};

export const fetchCommunityGroupStats = async (
  groupId: string,
  fromDate: number = 0,
  toDate: number = Math.floor(Date.now() / 1000),
): Promise<GroupStatsBoxScore[]> => {
  return axiosInstance
    .get(`/groups/stats/${groupId}`, {
      params: { from_date: fromDate, to_date: toDate },
    })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchCommunityGroupStats`:", err);
      return [];
    });
};

/** Per-session log for a single team player — no server-side date filtering, so filter client-side. */
export const fetchTeamPlayerLog = async (
  teamId: string,
  playerId: string,
): Promise<TeamPlayerSessionLog[]> => {
  return axiosInstance
    .get(`/teams/${teamId}/players/${playerId}/log`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchTeamPlayerLog`:", err);
      return [];
    });
};

export const updateTeamPlayer = async (
  teamId: string,
  playerId: string,
  body: EditTeamPlayerRequest,
): Promise<FBTeamPlayer | null> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/teams/${teamId}/roster/${playerId}/edit`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateTeamPlayer`:", err);
      return null;
    });
};

/**
 * Links a roster slot to an existing Hooper user (they replace the guest
 * player). The server also adds them to the team, as mobile's link does.
 */
export const linkTeamPlayerOrThrow = async (
  teamId: string,
  playerId: number,
  body: { callerId: string; userId: string },
): Promise<GroupRosterPlayer | null> =>
  axiosInstance.post(`/teams/${teamId}/roster/${playerId}/edit`, body).then((res) => res.data);

/**
 * Edits a roster slot's jersey, position or name. A name change renames the
 * user record behind the slot, so send names only for guest players.
 */
export const editTeamPlayerOrThrow = async (
  teamId: string,
  playerId: number,
  body: { callerId: string; jersey?: number; position?: string; firstName?: string; lastName?: string },
): Promise<GroupRosterPlayer | null> =>
  axiosInstance.post(`/teams/${teamId}/roster/${playerId}/edit`, body).then((res) => res.data);

export const fetchTeamSeasons = async (): Promise<FBTeamSeason[]> => {
  return axiosInstance
    .get(`/teams/seasons`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchTeamSeasons`:", err);
      return [];
    });
};

/**
 * Create a team, letting the failure through.
 *
 * `createTeam` below swallows the error and resolves null, which is what its
 * bulk-import callers want — one bad row must not stop the run. A form that
 * has to TELL the user why the create failed needs the server's `detail`
 * (a protected word in the name, say), so it calls this instead. One axios
 * call, two error policies.
 */
export const createTeamOrThrow = async (
  body: CreateTeamRequest,
): Promise<Group> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/teams/create`, body, config)
    .then((res) => res.data);
};

export const createTeam = async (
  body: CreateTeamRequest,
): Promise<Group | null> => {
  return createTeamOrThrow(body).catch((err) => {
    console.error("Error in `createTeam`:", err);
    return null;
  });
};

/**
 * Add a player to a team's roster, letting the failure through. The team
 * flow queues players and has to know which ones landed; `createTeamPlayer`
 * below keeps the swallow-and-null policy its bulk callers rely on.
 */
export const createTeamPlayerOrThrow = async (
  teamId: string,
  body: CreateTeamPlayerRequest,
): Promise<FBTeamPlayer> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/teams/${teamId}/roster/create`, body, config)
    .then((res) => res.data);
};

/** Adds every player or none; the server names any jersey that blocks the list. */
export const bulkCreateTeamPlayersOrThrow = async (
  teamId: string,
  body: BulkCreateTeamPlayersRequest,
): Promise<GroupRosterPlayer[]> =>
  axiosInstance.post(`/teams/${teamId}/roster/bulk-create`, body).then((res) => res.data);

export const createTeamPlayer = async (
  teamId: string,
  body: CreateTeamPlayerRequest,
): Promise<FBTeamPlayer | null> => {
  return createTeamPlayerOrThrow(teamId, body).catch((err) => {
    console.error("Error in `createTeamPlayer`:", err);
    return null;
  });
};

// The Roster page's reads and removal. They throw so the page can show a
// retry rather than an empty roster that isn't.

export const fetchTeamRosterOrThrow = async (teamId: string): Promise<GroupRosterPlayer[]> =>
  axiosInstance.get(`/teams/${teamId}/roster`).then((res) => res.data ?? []);

/** The Users (real and guest) linked to the roster's players, for their names. */
export const fetchTeamRosterUsersOrThrow = async (teamId: string): Promise<User[]> =>
  axiosInstance.get(`/teams/${teamId}/roster/users`).then((res) => res.data ?? []);

export const removeTeamPlayerOrThrow = async (
  teamId: string,
  playerId: number,
  body: RemoveTeamPlayerRequest,
): Promise<boolean> =>
  axiosInstance.post(`/teams/${teamId}/roster/${playerId}/remove`, body).then((res) => res.data);

// The team-creation flow's follow-ups. They throw so the flow can tell the
// person something didn't save rather than pretending it did.

export const editGroupDetailsOrThrow = async (body: EditGroupDetailsRequest): Promise<Group> =>
  axiosInstance.post(`/groups/edit/details`, body).then((res) => res.data);

/**
 * Renames a group. The route writes `description` whether or not it is sent
 * (absent = cleared), so callers pass the current one back.
 */
export const editGroupBasicOrThrow = async (body: EditGroupBasicRequest): Promise<Group> =>
  axiosInstance.post(`/groups/edit/basic`, body).then((res) => res.data);

export const editGroupLogoOrThrow = async (body: EditGroupLogoRequest): Promise<Group> =>
  axiosInstance.post(`/groups/edit/logo`, body).then((res) => res.data);

export const createGroupInvitationOrThrow = async (
  body: CreateGroupInvitationRequest,
): Promise<void> => {
  await axiosInstance.post(`/group-invitations`, body);
};

/** A Hooper account found by name or username (GET /user/search). */
export type HooperUserSearchResult = {
  userId: string;
  username: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
};

export const searchHooperUsersOrThrow = async (q: string, limit = 8): Promise<HooperUserSearchResult[]> =>
  axiosInstance.get(`/user/search`, { params: { q, limit } }).then((res) => res.data ?? []);

/** A team's outstanding invitation (GET /group-invitations, admins only). */
export type PendingGroupInvitation = {
  id: number;
  inviteeEmail?: string | null;
  inviteeUserId?: string | null;
  expiresAt: string;
};

export const fetchPendingGroupInvitationsOrThrow = async (
  teamId: string,
): Promise<PendingGroupInvitation[]> =>
  axiosInstance
    .get(`/group-invitations`, { params: { teamId: Number(teamId), status: "pending", limit: 100 } })
    .then((res) => res.data ?? []);

/** Cancels a pending invitation (the server marks it expired). */
export const cancelGroupInvitationOrThrow = async (invitationId: number): Promise<void> => {
  await axiosInstance.post(`/group-invitations/${invitationId}/expire`);
};

export const resendGroupInvitationOrThrow = async (invitationId: number): Promise<void> => {
  await axiosInstance.post(`/group-invitations/${invitationId}/resend-email`);
};

export const fetchCommunityCompetitionMembers = async (
  competitionId: string,
  page: number = 0,
  limit: number = 200,
): Promise<User[]> => {
  return axiosInstance
    .get(`/hub/community/competitions/${competitionId}/members`, {
      params: { page, limit },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchCommunityCompetitionMembers`:", err),
    );
};

export const fetchCommunityCompetitionTeams = async (
  competitionId: string,
  page: number = 0,
  limit: number = 100,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/hub/community/competitions/${competitionId}/teams`, {
      params: { page, limit },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchCommunityCompetitionTeams`:", err),
    );
};

export const fetchCommunityCompetitionSessions = async (
  competitionId: string,
  page: number = 0,
  limit: number = 100,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/hub/community/competitions/${competitionId}/sessions`, {
      params: { page, limit },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) =>
      console.error("Error in `fetchCommunityCompetitionSessions`:", err),
    );
};

export const addCompetitionTeams = async (
  competitionId: string,
  body: AddCompetitionTeamsRequest,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/competitions/${competitionId}/teams/add`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `addCompetitionTeams`:", err);
      return false;
    });
};

/**
 * Set session owner
 */
export const updateSessionOwner = async (
  id: string,
  userId: string,
): Promise<boolean> => {
  const body: UpdateSessionOwnerRequest = { userId: userId };
  return axiosInstance
    .post(`/admin/sessions/${id}/owner`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionOwner`", err));
};

/**
 * Set session state
 */
export const updateSessionStatus = async (
  id: string,
  status: SessionStatus,
  errorReason?: string,
): Promise<boolean> => {
  const body: UpdateSessionStatusRequest = {
    status: status.toString(),
    errorReason,
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/status`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionStatus`", err));
};

/**
 * Get sessions from user
 */
export const getSessionsFromUser = async (
  userId: string,
  page: number = 0,
  limit: number = 20,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/sessions/user/${userId}/${page}`, {
      params: { limit },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) => console.error("Error in `getSessionsFromUser`", err));
};

/**
 * Update video URL for session
 */
export const updateSessionVideoUrl = async (
  id: string,
  videoUrl: string,
): Promise<boolean> => {
  const body: UpdateSessionVideoUrlRequest = { videoUrl: videoUrl };
  return axiosInstance
    .post(`/admin/sessions/${id}/video/update`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionVideoUrl`", err));
};

/**
 * Repair a session's video source type and URL.
 *
 * Unlike its neighbours this rethrows instead of logging: the server's 400
 * detail explains exactly which combination was rejected, and the settings
 * form shows it to the admin.
 */
export const updateSessionVideoSource = async (
  id: string,
  body: UpdateVideoSourceRequest,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .patch(`/admin/sessions/${id}/video-source`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      throw new Error(
        err?.response?.data?.detail || "Failed to update video source",
      );
    });
};

/**
 * Update video FPS for session
 */
export const updateSessionVideoFps = async (
  id: string,
  videoFps: number = 30,
): Promise<boolean> => {
  const body: UpdateSessionVideoFpsRequest = { videoFps: videoFps };
  return axiosInstance
    .post(`/admin/sessions/${id}/fps/update`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionVideoFps`", err));
};

/**
 * Update session mode for session
 */
export const updateSessionMode = async (
  id: string,
  sessionMode: SessionMode,
): Promise<boolean> => {
  const body: UpdateSessionModeRequest = {
    sessionMode: sessionMode.toString(),
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/mode/update`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateSessionMode`", err);
      throw err;
    });
};

/**
 * Update session mode for session
 */
export const updateSessionType = async (
  id: string,
  sessionType: SessionType,
): Promise<boolean> => {
  const body: UpdateSessionTypeRequest = {
    sessionType: sessionType.toString(),
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/type/update`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateSessionType`", err);
      throw err;
    });
};

/**
 * Update session mode for session
 */
export const updateSessionRecordingSetup = async (
  id: string,
  recordingSetup: RecordingSetup,
): Promise<boolean> => {
  const body: UpdateSessionRecordingSetupRequest = {
    recordingSetup: recordingSetup.toString(),
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/setup/update`, body)
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `updateSessionRecordingSetup`", err),
    );
};

// These two deliberately let the request's rejection through instead of
// logging and resolving: `SettingsTab` has its own `.catch` for each, and a
// swallowed failure there showed a success toast for a write that never
// landed, then reverted on refresh.
export const updateSessionCameraSetup = async (
  id: string,
  cameraSetup: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/admin/sessions/${id}/camera/update`, { cameraSetup })
    .then((res) => res.data);
};

export const updateSessionHasJerseys = async (
  id: string,
  hasJerseys: boolean,
): Promise<boolean> => {
  return axiosInstance
    .post(`/admin/sessions/${id}/jerseys/update`, { hasJerseys })
    .then((res) => res.data);
};

/** Drop an admin jersey lock so the next run detects jerseys from video. */
export const redetectSessionHasJerseys = async (
  id: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/admin/sessions/${id}/jerseys/redetect`, {})
    .then((res) => res.data);
};

/**
 * Update point system for session
 */
export const updateSessionPointSystem = async (
  id: string,
  pointSystem: PointSystem,
): Promise<boolean> => {
  const body: UpdateSessionPointSystemRequest = {
    pointSystem: pointSystem,
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/points/update`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionPointSystem`", err));
};

/**
 * Update court type for session
 */
export const updateSessionCourtType = async (
  id: string,
  courtType: CourtType,
): Promise<boolean> => {
  const body: UpdateSessionCourtTypeRequest = {
    courtType: courtType,
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/court/update`, body)
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `updateSessionCourtTypeSystem`", err),
    );
};

/**
 * Update team names for session
 */
export const updateSessionTeamNames = async (
  id: string,
  team1Name: string,
  team2Name: string,
): Promise<boolean> => {
  const body: UpdateSessionTeamsRequest = {
    team1Name: team1Name,
    team2Name: team2Name,
  };
  return axiosInstance
    .post(`/admin/sessions/${id}/teams/update`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateSessionTeamNames`", err));
};

export const updateSessionAnnotatedScore = async (
  id: string,
  annotatedScore: number[],
  hasAnnotatedScore: boolean,
): Promise<boolean> => {
  return axiosInstance
    .post(`/admin/sessions/${id}/scores/annotated/edit`, {
      annotatedScore,
      hasAnnotatedScore,
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `updateSessionAnnotatedScore`", err),
    );
};

// === User-scoped session settings endpoints ===

export const editSessionDescription = async (
  sessionId: string,
  userId: string,
  description?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/description/${sessionId}`, { userId, description })
    .then((res) => res.data);
};

export const editSessionShareCode = async (
  sessionId: string,
  userId: string,
  shareCode: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/share-code/${sessionId}`, { userId, shareCode })
    .then((res) => res.data);
};

export const editSessionDisplayTime = async (
  sessionId: string,
  userId: string,
  displayTime?: number,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/display-time/${sessionId}`, { userId, displayTime })
    .then((res) => res.data);
};

export const editSessionPointSystem = async (
  sessionId: string,
  userId: string,
  pointSystem: PointSystem,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/points/${sessionId}`, { userId, pointSystem })
    .then((res) => res.data);
};

export const editSessionMode = async (
  sessionId: string,
  userId: string,
  sessionMode: SessionMode,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/mode/${sessionId}`, { userId, sessionMode })
    .then((res) => res.data);
};

export const editSessionType = async (
  sessionId: string,
  userId: string,
  sessionType: SessionType,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/type/${sessionId}`, { userId, sessionType })
    .then((res) => res.data);
};

export const editSessionCameraSetup = async (
  sessionId: string,
  userId: string,
  cameraSetup: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/camera/${sessionId}`, { userId, cameraSetup })
    .then((res) => res.data);
};

/** Court markings for this one recording; not spread to its siblings. */
export const editSessionCourtType = async (
  sessionId: string,
  userId: string,
  courtType: string,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/edit/court/${sessionId}`, { userId, courtType })
    .then((res) => res.data);

// Describes the camera rig, so the server applies it to every part of a
// multi-part game, like camera setup.
export const editSessionRecordingSetup = async (
  sessionId: string,
  userId: string,
  recordingSetup: string,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/edit/recording-setup/${sessionId}`, { userId, recordingSetup })
    .then((res) => res.data);

export const editSessionTeamHomeAway = async (
  sessionId: string,
  userId: string,
  teamHomeAway?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/team/homeaway/${sessionId}`, {
      userId,
      teamHomeAway,
    })
    .then((res) => res.data);
};

export const editSessionGroup = async (
  sessionId: string,
  userId: string,
  groupId?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/${sessionId}/group/edit`, { userId, groupId })
    .then((res) => res.data);
};

export const editSessionRecordingTeam = async (
  sessionId: string,
  userId: string,
  teamId?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/${sessionId}/team/recording/edit`, { userId, teamId })
    .then((res) => res.data);
};

export const editSessionOpponentTeam = async (
  sessionId: string,
  userId: string,
  teamId?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/${sessionId}/team/opponent/edit`, { userId, teamId })
    .then((res) => res.data);
};

export const editSessionOpponentName = async (
  sessionId: string,
  userId: string,
  name?: string,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/${sessionId}/team/opponent/name/edit`, { userId, name })
    .then((res) => res.data);
};

export const editSessionLock = async (
  sessionId: string,
  userId: string,
  locked: boolean,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/edit/lock/${sessionId}`, { userId, locked })
    .then((res) => res.data);
};

export type ShareDisplaySettings = {
  shareHideStats?: boolean;
  shareHideShotChart?: boolean;
};

// What the game's public share pages hide. The server applies it to every
// part of a multi-part game, so each part's links agree.
export const editSessionShareDisplay = async (
  sessionId: string,
  userId: string,
  settings: ShareDisplaySettings,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/edit/share-display/${sessionId}`, { userId, ...settings })
    .then((res) => res.data);

export const updateUserSessionAnnotatedScore = async (
  sessionId: string,
  userId: string,
  annotatedScore: number[],
  hasAnnotatedScore: boolean,
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/${sessionId}/scores/annotated/edit`, {
      userId,
      annotatedScore,
      hasAnnotatedScore,
    })
    .then((res) => res.data);
};

export const addSibling = async (
  body: {
    userId: string;
    sessionId: string;
    siblingSessionId: string;
    sessionPart?: string;
    role?: string;
  },
): Promise<{ success: boolean }> => {
  return axiosInstance
    .post(`/session/v4/siblings/add`, body)
    .then((res) => res.data);
};

export const removeSibling = async (
  body: { userId: string; sessionId: string; siblingSessionId: string },
): Promise<{ success: boolean }> => {
  return axiosInstance
    .post(`/session/v4/siblings/remove`, body)
    .then((res) => res.data);
};

export const fetchSessionSiblings = async (
  sessionId: string,
): Promise<{
  primary: Session;
  siblings: { session: Session; sessionPart: string; role?: string }[];
}> => {
  return axiosInstance
    .get(`/session/v4/siblings/${sessionId}`)
    .then((res) => ({
      ...res.data,
      primary: normalizeSessionIdentifier(res.data.primary),
      siblings: (res.data.siblings || []).map(
        (entry: { session: Session; sessionPart: string; role?: string }) => ({
          ...entry,
          session: normalizeSessionIdentifier(entry.session),
        }),
      ),
    }));
};

/**
 * Save auto-tag matches. The server validates each edit as a whole cluster
 * tag (FBClusterTagV4), so send the full clusters, as mobile does -- a
 * partial `{clusterId, userId}` is rejected.
 */
export const autoTagTeamSession = async (
  sessionId: string,
  userId: string,
  editedClusters: FBClusterTagV4[],
): Promise<boolean> => {
  return axiosInstance
    .post(`/session/v4/sessions/${sessionId}/roster/autotag-teams`, {
      userId,
      editedClusters,
    })
    .then((res) => res.data);
};

/**
 * Save a cluster's whole identity, roster link included, and move it to the
 * matching session roster player (mobile's reassign). The admin cluster edit
 * can't set or clear `teamPlayerId`, so roster tagging goes through here.
 */
export const reassignCluster = async (
  sessionId: string,
  taggerId: string,
  cluster: FBClusterTagV4,
): Promise<FBClusterTagV4> =>
  axiosInstance
    .post(`/session/v4/sessions/${sessionId}/roster/clusters/reassign`, {
      clusterId: cluster.clusterId,
      taggerId,
      teamId: cluster.teamId,
      userId: cluster.userId ?? null,
      name: cluster.name ?? null,
      nameId: cluster.nameId ?? null,
      jersey: cluster.jersey ?? null,
      teamV2Id: cluster.teamV2Id ?? null,
      teamPlayerId: cluster.teamPlayerId ?? null,
      ignore: cluster.ignore ?? false,
    })
    .then((res) => res.data);

/**
 * Assign teams: save which team each detected player is on. The server
 * stores `teamV2Id` only (see shared/teamAssignment).
 */
export const swapSessionTeams = async (
  sessionId: string,
  userId: string,
  edits: Array<{ clusterId: string; teamV2Id?: string }>,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/sessions/${sessionId}/roster/teams/swap`, { userId, edits })
    .then((res) => res.data);

/**
 * Both team rosters for auto-tag, from the same owner endpoints mobile uses:
 * they drop hidden players and keep jerseys as text, so "00" stays apart
 * from "0". A side with no team comes back empty.
 */
export const fetchSessionTeamRosters = async (
  sessionId: string,
  userId: string,
  /**
   * Pass the session's teams to name the players too: their names live on
   * the roster's linked accounts (see withRosterUserNames). Auto-tag only
   * needs jerseys and ids, so it leaves this out.
   */
  teams?: { recordingTeamId?: string; opponentTeamId?: string },
): Promise<{ recording: SessionTeamRosterPlayer[]; opponent: SessionTeamRosterPlayer[] }> => {
  const params = { userId, sessionId };
  // A roster whose names can't load still tags and still lists jerseys.
  const usersOf = (teamId?: string) =>
    teams && teamId
      ? fetchTeamRosterUsersOrThrow(teamId).catch(() => [] as User[])
      : Promise.resolve([] as User[]);
  const [recording, opponent, recordingUsers, opponentUsers] = await Promise.all([
    axiosInstance.get(`/session/team/recording/roster`, { params }),
    axiosInstance.get(`/session/team/opponent/roster`, { params }),
    usersOf(teams?.recordingTeamId),
    usersOf(teams?.opponentTeamId),
  ]);
  return {
    recording: withRosterUserNames(recording.data ?? [], recordingUsers),
    opponent: withRosterUserNames(opponent.data ?? [], opponentUsers),
  };
};

/**
 * Edit a shot for a session
 */
export const editShot = async (
  sessionId: string,
  shotId: string,
  body: EditShotRequest,
): Promise<FBShotV4> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/shots/${shotId}/edit`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `editShot`:", err);
      throw err;
    });
};

/**
 * Ignore (or restore) every shot starting inside a time window.
 *
 * One request for the whole window: the server writes the shots, their
 * ACTION_SHOT tags and their highlights in one transaction and recomputes
 * box scores once. Throws so the caller can leave local state untouched
 * rather than showing a segment as hidden that the server still has.
 */
export const batchIgnoreShotsInRange = async (
  sessionId: string,
  body: BatchIgnoreShotsRequest,
): Promise<BatchIgnoreShotsResponse> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/shots/batch/ignore`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `batchIgnoreShotsInRange`:", err);
      throw err;
    });
};

/**
 * Set a session's verified percent (propagates to its siblings).
 */
export const updateSessionVerifiedPercent = async (
  sessionId: string,
  body: UpdateSessionVerifiedPercentRequest,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/session/${sessionId}/verified/edit`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateSessionVerifiedPercent`:", err);
      throw err;
    });
};

/**
 * Create a shot for a session
 */
export const createShot = async (
  sessionId: string,
  body: CreateShotRequest,
): Promise<TagShotHighlight | undefined> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/shots/create`, body, config)
    .then((res) => unwrapEntity<TagShotHighlight>("createShot", res, ["shot", "tag"]))
    .catch((err) => {
      console.error("Error in `createShot`:", err);
      return undefined;
    });
};

/**
 * Edit a tag for a session
 */
export const editTag = async (
  sessionId: string,
  tagId: string,
  body: EditTagRequest,
): Promise<FBTagV4> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/tags/${tagId}/edit`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `EditTagRequest`:", err);
      throw err;
    });
};

/**
 * Create a tag for a session
 */
export const createTag = async (
  sessionId: string,
  body: CreateTagRequest,
): Promise<FBTagV4 | undefined> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/tags/create`, body, config)
    .then((res) => unwrapEntity<FBTagV4>("createTag", res, ["tagId"]))
    .catch((err) => {
      console.error("Error in `createTag`:", err);
      return undefined;
    });
};

/**
 * Edit a highlight for a session
 */
export const editHighlight = async (
  sessionId: string,
  highlightId: string,
  body: EditHighlightRequest,
): Promise<FBHighlightV4> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(
      `/hub/sessions/${sessionId}/highlights/${highlightId}/edit`,
      body,
      config,
    )
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `editHighlight`:", err);
      throw err;
    });
};

/**
 * Edit a cluster for a session
 */
export const editClusterTag = async (
  sessionId: string,
  clusterTagId: string,
  body: EditClusterTagRequest,
): Promise<FBClusterTagV4> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(
      `/hub/sessions/${sessionId}/clusters/${clusterTagId}/edit`,
      body,
      config,
    )
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `editClusterTag`:", err);
      throw err;
    });
};

/**
 * Create a tag for a session
 */
export const createClusterTag = async (
  sessionId: string,
  body: CreateClusterTagRequest,
): Promise<FBClusterTagV4 | undefined> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/clusters/create`, body, config)
    .then((res) => unwrapEntity<FBClusterTagV4>("createClusterTag", res, ["clusterId"]))
    .catch((err) => {
      console.error("Error in `createCluster`:", err);
      return undefined;
    });
};

// === Upload Endpoints ===

/**
 * Rejects with the axios error when the server refuses, so the caller can
 * show why (`serverMessage`): not a member of the team, out of recording
 * hours, an upload id already bound to something else.
 */
export const createSessionAndUploadVideo = async (
  body: CreateSessionRequest,
): Promise<Session | undefined> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/upload`, body, config)
    .then((res) => unwrapData<Session>("createSessionAndUploadVideo", res))
    .catch((err) => {
      console.error("Error in `createSessionAndUploadVideo`:", err);
      throw err;
    });
};

export const initiateUpload = async (
  sessionId: string,
  body: InitiateUploadRequest,
): Promise<InitiateUploadResponse> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/upload/initiate`, body, config)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `initiateUpload`:", err);
      throw err;
    });
};

export const shareSessionWeb = async (
  sessionId: string,
  body: ShareSessionRequest,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/share`, body, config)
    .then((res) => res.data);
};

export const unshareSessionWeb = async (
  sessionId: string,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/unshare`, {}, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `unshareSessionWeb`:", err));
};

/**
 * Publish (or update) a session's public web link as the signed-in user,
 * not as an admin. The server allows it for anyone involved in the game when
 * they or its team are Pro, and answers 403 with the reason otherwise.
 * Mutations rethrow so the caller can show that reason.
 */
export const shareSessionAsMember = async (
  sessionId: string,
  userId: string,
  body: ShareSessionRequest,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/web-share/${sessionId}`, { userId, ...body })
    .then((res) => res.data);

/** Take a session's public web link down as the signed-in user. */
export const unshareSessionAsMember = async (
  sessionId: string,
  userId: string,
): Promise<boolean> =>
  axiosInstance
    .post(`/session/v4/web-unshare/${sessionId}`, { userId })
    .then((res) => res.data);

export const addSessionTitle = async (
  sessionId: string,
  title?: string,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  const body: AddSessionTitleRequest = { customName: title };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/title`, body, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `addSessionTitle`:", err));
};

export const descriptionChange = async (
  sessionId: string,
  description?: string,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  const body: DescriptionChangeRequest = { description: description };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/description`, body, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `descriptionChange`:", err));
};

// === Annotation Endpoints ===

export const overrideHoop = async (
  sessionId: string,
  boxes: BoxAnnotation[],
): Promise<Session> => {
  const config = { headers: { "Content-Type": "application/json" } };
  const body: OverrideSessionHoopRequest = { boxes: boxes };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/hoop/override`, body, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `overrideHoop`:", err));
};

export const overrideCourt = async (
  sessionId: string,
  courts: CourtAnnotation[],
): Promise<Session> => {
  const config = { headers: { "Content-Type": "application/json" } };
  const body: OverrideSessionCourtRequest = { courts: courts };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/court/override`, body, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `overrideCourt`:", err));
};

// === Home Endpoints ===

/**
 * Fetches waiting sessions from the last month
 */
export const fetchUnresolvedQueueJobs = async (): Promise<FBQueueJob[]> => {
  return axiosInstance
    .get(`/admin/home/queue/jobs/unresolved`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchUnresolvedQueueJobs`:", err));
};

export const updateQueueJobStatus = async (
  queueId: string,
  status: "processed" | "errored",
): Promise<void> => {
  await axiosInstance.patch(`/admin/home/queue/jobs/${queueId}/status`, { status });
};

/**
 * Fetch basic summary stats
 */
export const fetchSummaryStats = async (): Promise<SummaryStats> => {
  return axiosInstance
    .get(`/admin/home/stats`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSummaryStats`:", err));
};

/**
 * Fetch public-API usage by organization + job health for the admin home
 * charts. `days` is the trailing window; the server clamps it to 1..90.
 */
export const fetchAdminApiStats = async (
  days: number = 30,
): Promise<AdminApiStats> => {
  return axiosInstance
    .get(`/admin/home/api-stats`, { params: { days } })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchAdminApiStats`:", err));
};

/**
 * Fetches sessions from the last day
 */
export const fetchRecentSessions = async (
  scale: string = "day",
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/home/sessions/recent`, { params: { scale: scale } })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) => console.error("Error in `fetchRecentSessions`:", err));
};

/**
 * Fetches waiting sessions from the last month
 */
export const fetchRecentWaitingSessions = async (
  scale: string = "month",
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/home/sessions/recent/waiting`, { params: { scale: scale } })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) =>
      console.error("Error in `fetchRecentWaitingSessions`:", err),
    );
};

/**
 * Fetches users who joined from the last week
 */
export const fetchRecentUsers = async (
  scale: string = "day",
): Promise<User[]> => {
  return axiosInstance
    .get(`/admin/home/users/recent`, { params: { scale: scale } })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchRecentUsers`:", err));
};

/**
 * Fetches sessions on a day
 */
export const fetchDaySessions = async (
  year: number,
  month: number,
  day: number,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/home/sessions/day`, {
      params: { year: year, month: month, day: day },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) => console.error("Error in `fetchDaySessions`:", err));
};

export const fetchAdminSessions = async (
  page: number = 0,
  limit: number = 20,
  status?: string,
  userQuery?: string,
  startTs?: number,
  endTs?: number,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/sessions`, {
      params: {
        page,
        limit,
        status: status || undefined,
        user_query: userQuery?.trim() || undefined,
        start_ts: startTs,
        end_ts: endTs,
      },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) => {
      console.error("Error in `fetchAdminSessions`:", err);
      return [];
    });
};

export const fetchAdminSessionsCount = async (
  status?: string,
  userQuery?: string,
  startTs?: number,
  endTs?: number,
): Promise<number> => {
  return axiosInstance
    .get(`/admin/sessions/filter/count`, {
      params: {
        status: status || undefined,
        user_query: userQuery?.trim() || undefined,
        start_ts: startTs,
        end_ts: endTs,
      },
    })
    .then((res) => Number(res.data) || 0)
    .catch((err) => {
      console.error("Error in `fetchAdminSessionsCount`:", err);
      return 0;
    });
};

/**
 * Fetches users on a day
 */
export const fetchDayUsers = async (
  year: number,
  month: number,
  day: number,
): Promise<User[]> => {
  return axiosInstance
    .get(`/admin/home/users/day`, {
      params: { year: year, month: month, day: day },
    })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchDayUsers`:", err));
};

export const fetchAdminUsers = async (
  page: number = 0,
  limit: number = 20,
  userQuery?: string,
  startTs?: number,
  endTs?: number,
): Promise<User[]> => {
  return axiosInstance
    .get(`/admin/users`, {
      params: {
        page,
        limit,
        user_query: userQuery?.trim() || undefined,
        start_ts: startTs,
        end_ts: endTs,
      },
    })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchAdminUsers`:", err);
      return [];
    });
};

export const fetchAdminUsersCount = async (
  userQuery?: string,
  startTs?: number,
  endTs?: number,
): Promise<number> => {
  return axiosInstance
    .get(`/admin/users/filter/count`, {
      params: {
        user_query: userQuery?.trim() || undefined,
        start_ts: startTs,
        end_ts: endTs,
      },
    })
    .then((res) => Number(res.data) || 0)
    .catch((err) => {
      console.error("Error in `fetchAdminUsersCount`:", err);
      return 0;
    });
};

export const enableUserAccountOrThrow = async (userId: string): Promise<void> => {
  await axiosInstance.post(`/admin/users/${encodeURIComponent(userId)}/enable`);
};

export const adminDisableUserAccountOrThrow = async (userId: string): Promise<void> => {
  await axiosInstance.post(`/admin/users/${encodeURIComponent(userId)}/disable`);
};

/**
 * Fetches session counts growing over the last year
 */
export const fetchSessionTimeline = async (
  scale: string = "month",
  n: number = 6,
  aggregate: boolean = false,
): Promise<TimelinePoint[]> => {
  return axiosInstance
    .get(`/admin/home/sessions/timeline`, {
      params: { scale: scale, n: n, aggregate: aggregate },
    })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionTimeline`:", err));
};

/**
 * Fetches user counts growing over the last year
 */
export const fetchUserTimeline = async (
  scale: string = "month",
  n: number = 6,
  aggregate: boolean = false,
): Promise<TimelinePoint[]> => {
  return axiosInstance
    .get(`/admin/home/users/timeline`, {
      params: { scale: scale, n: n, aggregate: aggregate },
    })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchUserTimeline`:", err));
};

/**
 * Fetches successful subscription counts over the last period.
 */
export const fetchSubscriptionTimeline = async (
  scale: string = "month",
  n: number = 6,
  aggregate: boolean = false,
): Promise<TimelinePoint[]> => {
  return axiosInstance
    .get(`/admin/home/subscriptions/timeline`, {
      params: { scale: scale, n: n, aggregate: aggregate },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchSubscriptionTimeline`:", err),
    );
};

/**
 * Fetches latest paid invoices for admin home.
 */
export const fetchRecentPaidInvoices = async (
  limit: number = 25,
): Promise<PaidInvoiceItem[]> => {
  return axiosInstance
    .get(`/admin/home/invoices/paid/recent`, { params: { limit } })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchRecentPaidInvoices`:", err));
};

export const fetchAdminInvoices = async (
  page: number = 1,
  pageSize: number = 25,
  filters: AdminInvoiceFilters = {},
): Promise<AdminInvoicePageResponse> => {
  const params: Record<string, string | number> = { page, page_size: pageSize };
  if (filters.dateFrom) params.date_from = Math.floor(new Date(filters.dateFrom).getTime() / 1000);
  if (filters.dateTo) {
    // Include the full day by setting to end-of-day.
    const d = new Date(filters.dateTo);
    d.setHours(23, 59, 59, 999);
    params.date_to = Math.floor(d.getTime() / 1000);
  }
  if (filters.status?.trim()) params.status = filters.status.trim();
  if (filters.userId?.trim()) params.user_id = filters.userId.trim();
  return axiosInstance
    .get(`/admin/invoices`, { params })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchAdminInvoices`:", err);
      return { items: [], page, pageSize, totalCount: 0, totalPages: 0, hasMore: false };
    });
};

export const issueAdminInvoiceRefund = async (
  invoiceId: string,
): Promise<AdminInvoiceRefundResponse> => {
  return axiosInstance
    .post(`/admin/invoices/${invoiceId}/refund`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `issueAdminInvoiceRefund`:", err);
      throw err;
    });
};

export const fetchAdminUserInvoiceHistory = async (
  userId: string,
): Promise<AdminInvoiceItem[]> => {
  return axiosInstance
    .get(`/admin/users/${userId}/invoices`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchAdminUserInvoiceHistory`:", err);
      throw err;
    });
};

/**
 * Fetches referrals ranking data for admin home.
 */
export const fetchReferralsRanking = async (
  limit: number = 25,
): Promise<ReferralsRankingResponse> => {
  return axiosInstance
    .get(`/admin/home/referrals/ranking`, { params: { limit } })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchReferralsRanking`:", err));
};

/**
 * Fetches referral signups vs activations by day and week comparison stats for admin home.
 */
export const fetchReferralHomeStats = async (): Promise<ReferralHomeStats> => {
  return axiosInstance
    .get(`/admin/home/referrals/stats`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchReferralHomeStats`:", err);
      return {
        referredTimeline: [],
        activeReferredTimeline: [],
        totalReferred: 0,
        totalActiveReferred: 0,
        referredThisWeek: 0,
        referredLastWeek: 0,
        activeReferredThisWeek: 0,
        activeReferredLastWeek: 0,
      };
    });
};

// === User Endpoints ===

export const fetchUserStats = async (userId: string): Promise<UserStats> => {
  return axiosInstance
    .get(`/admin/users/${userId}/stats`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchUserStats`:", err));
};

/**
 * Fetch a single user from that API
 * @param userId (string): ID of the user to fetch
 */
export const fetchUser = async (userId: string): Promise<User> => {
  return axiosInstance
    .get(`/hub/users/${userId}`)
    .then((res) => normalizeUserIdentifier(res.data))
    .catch((err) => console.error("Error in `fetchUser`", err));
};

/** Returns the numeric individual-group id for a user. */
export const fetchUserIndividualGroupId = async (
  userId: string
): Promise<number> =>
  axiosInstance
    .get(`/admin/users/${userId}/individual-group-id`)
    .then((res) => res.data.groupId as number);

/**
 * Fetches the currently authenticated user's own record (non-admin endpoint).
 * Use this when the caller may not have admin privileges (e.g. self sign-up).
 */
export const fetchSelfUser = async (userId: string): Promise<FBUser | null> => {
  return axiosInstance
    .get(`/user/${userId}`)
    .then((res) => normalizeOptionalUserIdentifier(res.data));
};

/**
 * Creates a backend user record for a freshly registered Firebase user.
 */
export const createUser = async (
  body: CreateUserRequest,
): Promise<FBUser> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance.post(`/user`, body, config).then((res) => res.data);
};

export const verifyUserCode = async (code: string): Promise<FBUser> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/user/verify-code`, { code }, config)
    .then((res) => res.data);
};

export const resendUserVerificationCode = async (): Promise<{
  sent: boolean;
  verified: boolean;
}> => {
  return axiosInstance
    .post(`/user/verification-code/resend`)
    .then((res) => res.data);
};

export const fetchUserMonthlyUsage = async (
  userId: string,
): Promise<SessionMonthlyUsage> => {
  return axiosInstance
    .get(`/session/users/${userId}/usage/monthly`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchUserMonthlyUsage`", err);
      throw err;
    });
};

export const fetchGroupMonthlyUsage = async (
  groupId: string,
): Promise<SessionMonthlyUsage> => {
  return axiosInstance
    .get(`/session/groups/${groupId}/usage/monthly`)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchGroupMonthlyUsage`", err);
      throw err;
    });
};

/**
 * Search for a user by ID or email.
 * @param query (string): User ID or email to search for
 * @returns (User) the user if found
 */
export const searchUser = async (query: string): Promise<User> => {
  return axiosInstance
    .get(`/admin/users/search`, { params: { q: query.trim() } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `searchUser`", err);
      throw err;
    });
};

export const fetchUserByShareCode = async (
  shareCode: string,
): Promise<User | undefined> => {
  const code = shareCode.trim();
  if (!code) return undefined;
  return axiosInstance
    .get(`/admin/users/share/${encodeURIComponent(code)}`)
    .then((res) => normalizeUserIdentifier(res.data))
    .catch(() => undefined);
};

/**
 * Claim the beta reward (free 30-day Pro trial for beta users)
 * @param userId (string): ID of the user claiming the reward
 * @returns (User) the updated user with Pro subscription
 */
export const claimBetaReward = async (userId: string): Promise<User> => {
  return axiosInstance
    .post(`/hub/users/${userId}/claim-beta-reward`)
    .then((res) => res.data);
};

/**
 * Grant a free 30-day Pro trial to any user (admin-only).
 * @param userId (string): ID of the user to grant the trial to
 * @returns (User) the updated user with Pro subscription
 */
export const giveUserFreeTrial = async (
  userId: string,
  options?: GiveUserFreeTrialRequest,
): Promise<User> => {
  return axiosInstance
    .post(`/admin/users/${userId}/free-trial`, options)
    .then((res) => res.data);
};

/**
 * Fetches all sessions belonging to a user from the API
 * @param userId (string): ID of the user
 * @param page (number): Page to start on
 * @param limit (number): Number of sessions to fetch
 */
export const fetchUserSessions = async (
  userId: string,
  page: number,
  limit: number,
): Promise<Session[]> => {
  return axiosInstance
    .get(`/admin/users/${userId}/sessions`, {
      params: { page: page, limit: limit },
    })
    .then((res) => res.data.map(normalizeAdminSessionIdentifiers))
    .catch((err) => console.error("Error in `fetchUserSessions`", err));
};

/**
 * Fetches session cards for a user's individual (Netflix-mode "Games")
 * account, from the user-facing (non-admin) cards endpoint.
 *
 * `GET /session/v4/cards/{userId}` is declared `-> Tuple[List[SessionCard], int]`,
 * so the JSON body is `[[...cards], totalCount]`. Unwrap defensively: a bare
 * array (or any shape a future change might send) passes through unchanged.
 *
 * `scope: "individual"` restricts results to sessions recorded under the
 * caller's individual group — otherwise this would list sessions from every
 * group the user belongs to. `type: "all"` keeps in-flight uploads
 * (created/uploaded/waiting/error) visible, matching the old admin list.
 * `fromDate` / `toDate` are optional epoch-second bounds on `createdAt`.
 */
export const fetchIndividualSessionCards = async (
  userId: string,
  page: number,
  limit: number,
  fromDate?: number,
  toDate?: number,
): Promise<SessionCard[]> => {
  return axiosInstance
    .get(`/session/v4/cards/${userId}`, {
      params: {
        page,
        limit,
        type: "all",
        scope: "individual",
        ...(fromDate ? { from_date: fromDate } : {}),
        ...(toDate ? { to_date: toDate } : {}),
      },
    })
    .then((res) => {
      const body = res.data;
      return Array.isArray(body?.[0]) ? body[0] : body;
    })
    .catch((err) => {
      console.error("Error in `fetchIndividualSessionCards`", err);
      // Rethrown, not []: an empty list here reads as "no games" and Home
      // would keep showing that until a reload.
      throw err;
    });
};

/**
 * Fetches session cards for a team (group) account, from the user-facing
 * cards endpoint. Unlike `/session/v4/cards/{userId}`, this endpoint returns
 * a bare `List[SessionCard]` — no tuple to unwrap. `type: "all"` keeps
 * in-flight uploads visible. `fromDate` / `toDate` are optional
 * epoch-second bounds on `createdAt`.
 */
export const fetchTeamSessionCards = async (
  groupId: string,
  page: number,
  limit: number,
  fromDate?: number,
  toDate?: number,
): Promise<SessionCard[]> => {
  return axiosInstance
    .get(`/groups/cards/${groupId}`, {
      params: {
        page,
        limit,
        type: "all",
        ...(fromDate ? { from_date: fromDate } : {}),
        ...(toDate ? { to_date: toDate } : {}),
      },
    })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchTeamSessionCards`", err);
      // Rethrown, not []: an empty list here reads as "no games" and Home
      // would keep showing that until a reload.
      throw err;
    });
};

/**
 * Fetches user counts growing over the last year
 */
export const fetchUserSessionsTimeline = async (
  userId: string,
  n: number = 30,
): Promise<TimelinePoint[]> => {
  return axiosInstance
    .get(`/admin/users/${userId}/timeline`, {
      params: { n: n },
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `fetchUserSessionsTimeline`:", err),
    );
};

// === Session Endpoints ===

// `/public/session/{id}` (and its `/owner` sibling) must stay unauthenticated —
// they're the live data source for the mobile-share preview page (the Hub's
// `/shared/:id` preview, formerly web/app's), and mobile never sets
// `web_shared`. Every other gated public-session call now lives
// behind `/public/share/{sessionId}/...` and requires the `X-Share-Token`
// header minted by `POST /public/share/{sessionId}/unlock`.
const PUBLIC_SESSION_ENDPOINT = "/public/session";
const PUBLIC_SHARE_ENDPOINT = "/public/share";

const SHARE_TOKEN_STORAGE_PREFIX = "hooper.shareToken.";

/**
 * Reads a previously-persisted share unlock token for a session.
 *
 * Persistence itself (sessionStorage, keyed by session id) is written by
 * whoever successfully calls `unlockPublicSession` — see
 * `SharePasswordGate`'s `onUnlock` wiring — this getter just knows where to
 * look it up so every gated public-share call below can attach it.
 */
const getShareToken = (sessionId: string): string | null => {
  try {
    return sessionStorage.getItem(`${SHARE_TOKEN_STORAGE_PREFIX}${sessionId}`);
  } catch {
    // Safari private browsing (and similar) throws on access even though
    // `sessionStorage` is defined — degrade to "no token" rather than crash.
    return null;
  }
};

/**
 * Persists a share unlock token for a session so subsequent gated
 * public-share calls for that session automatically attach it.
 */
export const setShareToken = (sessionId: string, token: string): void => {
  try {
    sessionStorage.setItem(`${SHARE_TOKEN_STORAGE_PREFIX}${sessionId}`, token);
  } catch {
    // See getShareToken — storage may be unavailable/blocked.
  }
};

/**
 * Clears a persisted share unlock token, e.g. after a 401 from a gated call.
 * Not yet wired into any gated call's 401 handling — currently unused. A
 * remount re-runs the auto-unlock probe in page.tsx, so an expired token
 * self-recovers on reload; without a caller here it just fails silently on
 * the one gated call it happens to hit until then. Follow-up: call this from
 * a shared 401 handler and re-show SharePasswordGate rather than degrading
 * that one call silently.
 */
export const clearShareToken = (sessionId: string): void => {
  try {
    sessionStorage.removeItem(`${SHARE_TOKEN_STORAGE_PREFIX}${sessionId}`);
  } catch {
    // See getShareToken — storage may be unavailable/blocked.
  }
};

/** Axios request config attaching the `X-Share-Token` header, if one is persisted. */
const shareTokenConfig = (sessionId: string) => {
  const token = getShareToken(sessionId);
  return token ? { headers: { "X-Share-Token": token } } : undefined;
};

export const fetchPublicSession = async (
  sessionId: string,
): Promise<Session> => {
  return axiosInstance
    .get(`${PUBLIC_SESSION_ENDPOINT}/${sessionId}`)
    .then((res) => normalizeAdminSessionIdentifiers(res.data));
};

/**
 * Ungated owner of a session (`UserBrief`), paired with `fetchPublicSession`.
 * Backs the `/shared/:id` preview shown for sessions that aren't
 * `web_shared` (what mobile's Share button links to).
 */
export const fetchPublicSessionOwner = async (
  sessionId: string,
): Promise<PublicUserBrief> => {
  return axiosInstance
    .get(`${PUBLIC_SESSION_ENDPOINT}/${encodeURIComponent(sessionId)}/owner`)
    .then((res) => res.data as PublicUserBrief);
};

/** Ungated `UserBrief` by id, for a share link's `?referrerId=`. */
export const fetchPublicUser = async (
  userId: string,
): Promise<PublicUserBrief> => {
  return axiosInstance
    .get(`/public/user/${encodeURIComponent(userId)}`)
    .then((res) => res.data as PublicUserBrief);
};

/** Unauthenticated inviter meta for a referral link. Null when it can't be fetched. */
export const fetchReferralMeta = async (
  code: string,
): Promise<ReferralMeta | null> => {
  return axiosInstance
    .get(`/referrals/meta/${encodeURIComponent(code)}`)
    .then((res) => res.data as ReferralMeta)
    .catch((err) => {
      console.error("Error in `fetchReferralMeta`:", err);
      return null;
    });
};

/** The signed-in user's referral code (minted on first ask). Null when it can't be fetched. */
export const fetchMyReferralCode = async (): Promise<string | null> => {
  return axiosInstance
    .get(`/referrals/referral-code`)
    .then((res) => (res.data?.referralCode as string | undefined) || null)
    .catch((err) => {
      console.error("Error in `fetchMyReferralCode`:", err);
      return null;
    });
};

/**
 * Unauthenticated team card for a team share code (`/t/:code`). Resolves
 * private and hidden teams too: the code is the invite. Null on any failure
 * (unknown code included).
 */
export const fetchPublicTeamByShareCode = async (
  code: string,
): Promise<PublicTeamCard | null> => {
  return axiosInstance
    .get(`/public/team/share/${encodeURIComponent(code)}`)
    .then((res) => res.data as PublicTeamCard)
    .catch((err) => {
      console.error("Error in `fetchPublicTeamByShareCode`:", err);
      return null;
    });
};

/**
 * Unauthenticated card behind a guest-invite link (`/join/:guestId`). Null
 * when the server 404s it (a wrong token, or an invite already accepted or
 * withdrawn); any other failure rejects, so the page can offer a retry
 * instead of calling a live link dead.
 */
export const fetchGuestInvite = async (
  guestId: string,
  token: string,
): Promise<GuestInvitePreview | null> => {
  return axiosInstance
    .get(`/public/guest-invites/${encodeURIComponent(guestId)}`, {
      params: { token },
    })
    .then((res) => res.data as GuestInvitePreview)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      console.error("Error in `fetchGuestInvite`:", err);
      throw err;
    });
};

/**
 * Moves the games the invited guest was tagged in onto the signed-in account.
 * Rejects with the server's `detail.code` (e.g. `OWN_INVITE`).
 */
export const acceptGuestInvite = async (
  guestId: string,
  token: string,
): Promise<{ sessionCount: number; trialEndsAt?: string | null }> => {
  return axiosInstance
    .post(`/user/guest-invites/${encodeURIComponent(guestId)}/accept`, { token })
    .then((res) => res.data);
};

/**
 * Exchanges the share password for an unlock token that gates every
 * `/public/share/{sessionId}/...` call below (attached as `X-Share-Token`).
 * Persists the token via `setShareToken` before returning — callers don't
 * need to remember to do this separately, so a gated call can never run
 * against a successfully-unlocked session with no token attached.
 */
export const unlockPublicSession = async (
  sessionId: string,
  password: string,
): Promise<{ unlockToken: string }> => {
  return axiosInstance
    .post(`${PUBLIC_SHARE_ENDPOINT}/${sessionId}/unlock`, { password })
    .then((res) => {
      const data = res.data as { unlockToken: string };
      setShareToken(sessionId, data.unlockToken);
      return data;
    });
};

export const fetchPublicSessionRecorder = async (
  sessionId: string,
  isPartner: boolean,
): Promise<User> => {
  const ownerType = isPartner ? "partner" : "owner";
  return axiosInstance
    .get(
      `${PUBLIC_SHARE_ENDPOINT}/${sessionId}/${ownerType}`,
      shareTokenConfig(sessionId),
    )
    .then((res) => {
      if (!isPartner) return normalizeUserIdentifier(res.data) as User;

      const id = res.data.adminPartnerId;
      return {
        ...res.data,
        id,
        userId: id,
        role: "guest",
      } as User;
    });
};

export const fetchPublicSessionTags = async (
  sessionId: string,
): Promise<FBTagV4[]> => {
  return axiosInstance
    .get(`${PUBLIC_SHARE_ENDPOINT}/${sessionId}/tags`, shareTokenConfig(sessionId))
    .then((res) => res.data);
};

export const fetchPublicSessionHighlights = async (
  sessionId: string,
): Promise<FBHighlightV4[]> => {
  return axiosInstance
    .get(
      `${PUBLIC_SHARE_ENDPOINT}/${sessionId}/highlights`,
      shareTokenConfig(sessionId),
    )
    .then((res) => res.data);
};

export const fetchPublicSessionShots = async (
  sessionId: string,
): Promise<FBShotV4[]> => {
  return axiosInstance
    .get(`${PUBLIC_SHARE_ENDPOINT}/${sessionId}/shots`, shareTokenConfig(sessionId))
    .then((res) => res.data);
};

export const fetchPublicSessionStats = async (
  sessionId: string,
): Promise<PublicSessionStats> => {
  return axiosInstance
    .get(
      `${PUBLIC_SHARE_ENDPOINT}/${sessionId}/stats`,
      shareTokenConfig(sessionId),
    )
    .then((res) => ({
      ...res.data,
      taggedUsers: (res.data.taggedUsers ?? []).map(normalizeUserIdentifier),
    }));
};

/**
 * Fetch a single session from the API. Rejects on any failure (403, 404,
 * 5xx, network), like fetchPublicSession.
 * @param sessionId (string): ID of the session to fetch
 */
export const fetchSession = async (sessionId: string): Promise<Session> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}`)
    .then((res) => normalizeSessionIdentifier(res.data));
};

export const fetchSessionIdByShareCode = async (
  shareCode: string,
): Promise<string | undefined> => {
  const code = shareCode.trim();
  if (!code) return undefined;
  return axiosInstance
    .get(`/admin/sessions/share/${encodeURIComponent(code)}`)
    .then((res) => String(normalizeSessionIdentifier(res.data).id))
    .catch(() => undefined);
};

export const uploadAdminSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
  language: StatSheetLanguage,
  file: File,
): Promise<StatSheetRevision> => {
  const body = new FormData();
  body.append("file", file);
  body.append("language", language);
  body.append("run_version", String(runVersion));
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/stat-sheets`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => response.data);
};

export const fetchAdminSessionStatSheetPreview = async (
  sessionId: string,
  statSheetId: number,
): Promise<StatSheetPreview> =>
  axiosInstance
    .get(`/admin/sessions/${sessionId}/stat-sheets/${statSheetId}`)
    .then((response) => response.data);

const filenameFromContentDisposition = (
  disposition: string | undefined,
  fallback: string,
) => {
  if (!disposition) return fallback;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallback;
};

const downloadStatSheetSource = async (
  url: string,
  fallbackFilename: string,
): Promise<void> => {
  const response = await axiosInstance.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filenameFromContentDisposition(
    response.headers?.["content-disposition"],
    fallbackFilename,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const downloadAdminSessionStatSheetSource = async (
  sessionId: string,
  statSheetId: number,
  fallbackFilename = "stat-sheet",
): Promise<void> =>
  downloadStatSheetSource(
    `/admin/sessions/${sessionId}/stat-sheets/${statSheetId}/source`,
    fallbackFilename,
  );

export const fetchPendingAdminSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
): Promise<StatSheetRevision | null> => {
  const response = await axiosInstance.get(
    `/admin/sessions/${sessionId}/stat-sheets/pending`,
    { params: { run_version: runVersion } },
  );
  return response.status === 204 ? null : response.data;
};

export const fetchActiveAdminSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
): Promise<ConfirmedStatSheet | null> => {
  const response = await axiosInstance.get(
    `/admin/sessions/${sessionId}/stat-sheets/active`,
    { params: { run_version: runVersion } },
  );
  return response.status === 204 ? null : response.data;
};

export const confirmAdminSessionStatSheet = async (
  sessionId: string,
  statSheetId: number,
  body: ConfirmStatSheetRequest,
): Promise<ConfirmedStatSheet> =>
  axiosInstance
    .post(
      `/admin/sessions/${sessionId}/stat-sheets/${statSheetId}/confirm`,
      body,
    )
    .then((response) => response.data);

export const updateAdminSessionStatSheetPayload = async (
  sessionId: string,
  statSheetId: number,
  payload: StatSheetPayload,
): Promise<StatSheetRevision> =>
  axiosInstance
    .post(
      `/admin/sessions/${sessionId}/stat-sheets/${statSheetId}/payload`,
      payload,
    )
    .then((response) => response.data);

export const cancelAdminSessionStatSheet = async (
  sessionId: string,
  statSheetId: number,
): Promise<void> => {
  await axiosInstance.post(
    `/admin/sessions/${sessionId}/stat-sheets/${statSheetId}/cancel`,
  );
};

export const uploadSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
  language: StatSheetLanguage,
  file: File,
): Promise<StatSheetRevision> => {
  const body = new FormData();
  body.append("file", file);
  body.append("language", language);
  body.append("run_version", String(runVersion));
  return axiosInstance
    .post(`/session/v4/sessions/${sessionId}/stat-sheets`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => response.data);
};

export const fetchSessionStatSheetPreview = async (
  sessionId: string,
  statSheetId: number,
): Promise<StatSheetPreview> =>
  axiosInstance
    .get(`/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}`, {
      params: { include_team_options: true },
    })
    .then((response) => response.data);

export const downloadSessionStatSheetSource = async (
  sessionId: string,
  statSheetId: number,
  fallbackFilename = "stat-sheet",
): Promise<void> =>
  downloadStatSheetSource(
    `/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}/source`,
    fallbackFilename,
  );

export const fetchPendingSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
): Promise<StatSheetRevision | null> => {
  const response = await axiosInstance.get(
    `/session/v4/sessions/${sessionId}/stat-sheets/pending`,
    { params: { run_version: runVersion } },
  );
  return response.status === 204 ? null : response.data;
};

export const fetchActiveSessionStatSheet = async (
  sessionId: string,
  runVersion: number,
): Promise<ConfirmedStatSheet | null> => {
  const response = await axiosInstance.get(
    `/session/v4/sessions/${sessionId}/stat-sheets/active`,
    { params: { run_version: runVersion } },
  );
  return response.status === 204 ? null : response.data;
};

export const confirmSessionStatSheet = async (
  sessionId: string,
  statSheetId: number,
  body: ConfirmStatSheetRequest,
): Promise<ConfirmedStatSheet> =>
  axiosInstance
    .post(
      `/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}/confirm`,
      body,
    )
    .then((response) => response.data);

export const updateSessionStatSheetPayload = async (
  sessionId: string,
  statSheetId: number,
  payload: StatSheetPayload,
): Promise<StatSheetRevision> =>
  axiosInstance
    .post(
      `/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}/payload`,
      payload,
    )
    .then((response) => response.data);

export const cancelSessionStatSheet = async (
  sessionId: string,
  statSheetId: number,
): Promise<void> => {
  await axiosInstance.post(
    `/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}/cancel`,
  );
};

export const removeSessionStatSheet = async (
  sessionId: string,
  statSheetId: number,
): Promise<void> => {
  await axiosInstance.post(
    `/session/v4/sessions/${sessionId}/stat-sheets/${statSheetId}/remove`,
  );
};

/**
 * Fetch the recorder user for the session
 * @param sessionId (string): ID of the session to fetch
 */
export const fetchSessionRecorder = async (
  sessionId: string,
): Promise<User | undefined> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/recorder`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionRecorder`", err));
};

/**
 * Trigger the referral activation check for a user.
 * Same endpoint mobile uses; the `userId` body runs the check on that user's
 * behalf, which the server allows only for an admin caller.
 */
export const activateReferral = async (userId: string): Promise<void> => {
  return axiosInstance
    .post("/referrals/activate", { userId })
    .then(() => {})
    .catch((err) => console.error("Error in `activateReferral`", err));
};

/**
 * Fetch action tags for the session
 * @param sessionId
 */
export const fetchSessionTags = async (
  sessionId: string,
): Promise<FBTagV4[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/tags`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionTags`", err));
};

/**
 * Fetch highlights for the session
 * @param sessionId
 */
export const fetchSessionHighlights = async (
  sessionId: string,
): Promise<FBHighlightV4[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/highlights`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionHighlights`", err));
};

/**
 * Fetch shots for the session
 * @param sessionId
 */
export const fetchSessionShots = async (
  sessionId: string,
): Promise<FBShotV4[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/shots`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionShots`", err));
};

/**
 * Fetch clusters for the session
 * @param sessionId
 */
export const fetchSessionClusters = async (
  sessionId: string,
): Promise<FBClusterTagV4[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/clusters`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionClusters`", err));
};

/**
 * Fetch tagged players for the session
 * @param sessionId
 */
export const fetchSessionTaggedUsers = async (
  sessionId: string,
): Promise<User[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/tagged/users`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionTaggedUsers`", err));
};

/**
 * Fetch tagged names for the session
 * @param sessionId
 */
export const fetchSessionTaggedNames = async (
  sessionId: string,
): Promise<FBPlayerName[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/tagged/names`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionTaggedNames`", err));
};

export const fetchSessionSavedNames = async (
  sessionId: string,
): Promise<FBPlayerName[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/names`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionSavedNames`", err));
};

export const createSessionSavedName = async (
  sessionId: string,
  body: CreateSessionPlayerNameRequest,
): Promise<FBPlayerName | undefined> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/names/new`, body, config)
    .then((res) => unwrapEntity<FBPlayerName>("createSessionSavedName", res, ["nameId", "name"]))
    .catch((err) => {
      console.error("Error in `createSessionName`:", err);
      return undefined;
    });
};

export const deleteSessionSavedName = async (
  sessionId: string,
  nameId: string,
): Promise<boolean> => {
  const config = { headers: { "Content-Type": "application/json" } };
  return axiosInstance
    .post(`/admin/sessions/${sessionId}/names/${nameId}/delete`, {}, config)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `deleteSessionName`:", err));
};

/**
 * Fetch keyframes for the session
 * @param sessionId
 * @note used for tagging
 */
export const fetchSessionKeyframes = async (
  sessionId: string,
): Promise<FBKeyframeV4[]> => {
  return axiosInstance
    .get(`/hub/sessions/${sessionId}/keyframes`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `fetchSessionKeyframes`", err));
};

/**
 * Add editor to a session
 * @param sessionId (string)
 * @param body (AddSessionEditorRequest)
 * * @param version
 */
export const addSessionEditor = async (
  sessionId: string,
  body: AddSessionEditorRequest,
): Promise<boolean> => {
  return axiosInstance
    .post(`/hub/sessions/${sessionId}/editors`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `addSessionEditor`:", err);
    });
};

/**
 * Fetch queue tasks
 */
export const sendParentSessionReadyEmail = async (
  sessionId: string,
  email: string,
): Promise<void> => {
  return axiosInstance
    .post(`/admin/sendParentSessionReadyEmail`, {
      sessionId,
      email,
    })
    .then((res) => res.data)
    .catch((err) =>
      console.error("Error in `sendParentSessionReadyEmail`", err),
    );
};

/**
 * Login to hub using uuid stored at local storage
 * @param uuid
 */
export const adminKeepAlive = async (uuid: string): Promise<any> => {
  return axiosInstance
    .get(`/admin/login-check/${uuid}`)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `adminKeepAlive`", err));
};

// === Checkout Endpoints ===

/** Localized Pro prices for the paywall — what Checkout will actually charge. */
export const fetchProPricing = async (): Promise<ProPricing> =>
  axiosInstance
    .get("/checkout/pro-pricing")
    .then((res) => res.data as ProPricing);

/**
 * Create a PRO subscription checkout session to be used in a webview/browser
 */
export const createProSubscriptionCheckoutSession = async (params?: {
  successUrl?: string;
  cancelUrl?: string;
  // Attaches the subscription to this group instead of the payer's individual
  // group. The server enforces ownership itself (403 if not the owner).
  groupId?: string;
}): Promise<{ url: string } | undefined> => {
  const user = auth.currentUser;
  const token = await user?.getIdToken();

  return axiosInstance
    .post(`/checkout/pro-subscription-session`, params || {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) =>
      unwrapEntity<{ url: string }>(
        "createProSubscriptionCheckoutSession",
        res,
        ["url"],
      ),
    )
    .catch((err) => {
      console.error("Error in `createProSubscriptionCheckoutSession`", err);
      // Rethrow: swallowing here resolved the promise to `undefined`, so the
      // caller's catch never ran and its loading spinner span forever. Reads
      // may degrade to an empty result; mutations must surface the failure.
      throw err;
    });
};

/**
 * Stripe Customer Portal — cancel, change payment method, download invoices.
 *
 * `successUrl` is not optional in practice: the server falls back to the
 * mobile deep link (`hooper://settings`), which strands a browser user.
 */
export const createPortalSession = async (
  successUrl: string,
): Promise<{ url: string }> => {
  return axiosInstance
    .post(`/checkout/portal-session`, { successUrl })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `createPortalSession`", err);
      throw err;
    });
};

/**
 * Live extra-hours pricing. Admin-editable server-side, so the buy dialog must
 * quote from here rather than from a hardcoded rate.
 */
export const fetchExtraHoursConfig =
  async (): Promise<ExtraHoursPurchaseConfig> => {
    return axiosInstance
      .get(`/checkout/extra-hours-config`)
      .then((res) => res.data);
  };

/**
 * Browser checkout for extra processing hours. `groupId` attaches them to a
 * team (owner-only server-side); omitted buys for the individual plan.
 *
 * `successUrl`/`cancelUrl` are not optional in practice, for the same reason
 * as `createPortalSession`: the server falls back to the mobile deep links
 * (`hooper://payment-success`), which a browser cannot follow, so the buyer
 * lands nowhere and the fulfilment poll never runs.
 */
export const createExtraHoursCheckoutSession = async (
  hours: number,
  groupId: string | undefined,
  returnUrls: { successUrl: string; cancelUrl: string },
): Promise<ExtraHoursCheckoutSession> => {
  const body = groupId
    ? { hours, groupId, ...returnUrls }
    : { hours, ...returnUrls };
  return axiosInstance
    .post(`/checkout/extra-hours-session`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `createExtraHoursCheckoutSession`", err);
      throw err;
    });
};

/**
 * Fulfilment for one checkout. Credits are minted by the Stripe webhook, so
 * the hours are not there the instant the buyer lands back on the hub.
 */
export const fetchExtraHoursCheckoutStatus = async (
  sessionId: string,
): Promise<{ status: "pending" | "granted" | "expired" }> => {
  return axiosInstance
    .get(
      `/checkout/extra-hours-session/${encodeURIComponent(sessionId)}/status`,
    )
    .then((res) => res.data);
};

/**
 * Groups (and, with `includeTeams`, teams) the user belongs to or owns.
 *
 * The user-facing counterpart to `fetchUserCommunityTeams`, which is an
 * `/admin/...` route and 403s for a non-admin.
 *
 * Rejects rather than returning `[]` on failure: callers use this list to
 * decide whether a stored active account still exists, and a swallowed error
 * is indistinguishable from "you belong to nothing" — which would silently
 * evict a user from a team they are still in. See `ActiveAccountProvider`.
 */
export const fetchMyGroups = async (
  userId: string,
  includeTeams: boolean = false,
  page: number = 0,
  limit: number = 100,
): Promise<Group[]> => {
  return axiosInstance
    .get(`/groups`, { params: { userId, includeTeams, page, limit } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `fetchMyGroups`", err);
      throw err;
    });
};

// === Protected words endpoints ===

/**
 * Fetch protected words page
 */
export type ProtectedWordsPageResponse = {
  items: FBAdminProtectedWord[];
  total: number;
};

export const fetchProtectedWords = async (
  page: number,
  limit: number,
): Promise<ProtectedWordsPageResponse> => {
  return axiosInstance
    .get(`/admin/protected-words/${page}/${limit}`)
    .then((res) => res.data as ProtectedWordsPageResponse)
    .catch((err) => {
      console.error("Error in `fetchProtectedWords`", err);
      throw err;
    });
};

/**
 * Update a protected word's active state
 */
export const updateProtectedWord = async (
  word: FBAdminProtectedWord,
): Promise<boolean> => {
  const body = {
    protectedWordId: word.protectedWordId,
    isActive: word.isActive,
  };

  return axiosInstance
    .post(`/admin/protected-word/update`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateProtectedWords`", err));
};

/**
 * Create protected words
 * @param body (words)
 */

export const createProtectedWords = async (body: {
  words: string[];
}): Promise<string[] | undefined> => {
  return axiosInstance
    .post(`/admin/protected-words/create`, body)
    .then((res) => unwrapData<string[]>("createProtectedWords", res))
    .catch((err) => {
      console.error("Error in `createProtectedWords`:", err);
      return undefined;
    });
};

// === Streaming highlights ===

export const downloadHighlight = async (highlightId: string): Promise<void> => {
  const response = await axiosInstance.get(
    `/admin/highlight/${highlightId}/download`,
    {
      responseType: "blob", // Important: tells axios to expect binary data
    },
  );
  // Debug: Check the blob size
  console.log("Blob size:", response.data.size);
  console.log("Response headers:", response.headers);

  // Verify we got a valid blob
  if (!response.data || response.data.size === 0) {
    throw new Error("Received empty video file");
  }
  // Create a temporary URL for the blob
  const blobUrl = window.URL.createObjectURL(response.data);
  // Create and trigger download
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `highlight_${highlightId}.mp4`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Clean up the blob URL
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Download multiple videos as a zip file
 * @param file_urls,session_id
 * @param body (file_urls, session_id)
 */
export const downloadVideosOnZip = async (body: {
  file_urls: string[];
  session_id: string;
}): Promise<Blob> => {
  try {
    const res = await axiosInstance.post(`/download/zip`, body, {
      responseType: "blob",
    });
    return res.data;
  } catch (err) {
    console.error("Error in `downloadVideosOnZip`", err);
    throw err;
  }
};

/**
 * Download multiple videos as a compilation video
 * @param file_urls,session_id
 * @param body (file_urls, session_id)
 */
export const downloadVideosCompilation = async (body: {
  file_urls: string[];
  session_id: string;
}): Promise<string> => {
  try {
    const downloadUrl = await axiosInstance
      .post(`/download/compilation`, body)
      .then((res) => res.data);

    if (!downloadUrl) {
      throw new Error("Server did not return a download URL");
    }

    if (typeof window !== "undefined") {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch compilation: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `session_${body.session_id}_compilation.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }

    return downloadUrl;
  } catch (err) {
    console.error("Error in `downloadVideosCompilation`", err);
    throw err;
  }
};

/**
 * Records a download against the free plan's limit, as the app does. The
 * game's owning group (`groupId`) decides whether it is Pro. Undefined when
 * the request fails, so a network error isn't mistaken for the limit.
 */
export const createDownload = async (
  entityId: string,
  entityType: string,
  count: number = 1,
  includeWatermark: boolean = true,
  groupId?: string,
): Promise<CreateDownloadResponse | undefined> => {
  return axiosInstance
    .post(`/download/`, {
      entityId,
      entityType,
      count,
      includeWatermark,
      groupId,
    })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `createDownload`", err);
      return undefined;
    });
};

export const updateUserSubscriptionType = async (
  id: string,
  subscriptionType: SubscriptionType,
): Promise<User> => {
  const body: UpdateUserSubscriptionTypeRequest = {
    subscriptionType: subscriptionType,
  };
  return axiosInstance
    .post(`/admin/users/${id}/subscription`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateUserSubscriptionType`:", err);
      throw err;
    });
};

export const updateUserType = async (
  id: string,
  userType: string,
): Promise<FBUser> => {
  const body: UpdateUserTypeRequest = {
    userType,
  };

  return axiosInstance
    .post(`/admin/users/${id}/user-type`, body)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `updateUserType`:", err);
      throw err;
    });
};

export const updateUserSegmentType = async (
  id: string,
  segmentType: UserSegmentType,
): Promise<User> => {
  const body: UpdateUserSegmentTypeRequest = {
    segmentType: segmentType,
  };
  return axiosInstance
    .post(`/admin/users/${id}/segment`, body)
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateUserSegmentType`:", err));
};

// === User Profile Endpoints ===

export const updateUserProfilePicture = async (
  userId: string,
  profilePicture: string,
) => {
  return axiosInstance
    .post("/user/picture", { userId, userIdToUpdate: userId, profilePicture })
    .then((res) => res.data)
    .catch((err) => {
      // Rethrow so Settings shows the failure instead of "updated".
      console.error("Error in `updateUserProfilePicture`:", err);
      throw err;
    });
};

// === Onboarding Endpoints ===
// These throw instead of logging and resolving undefined: onboarding has to
// know whether a save landed before it moves the person to the next step.

const errorDetail = (err: any): unknown => err?.response?.data?.detail;

export class UsernameTakenError extends Error {
  constructor() {
    super("Username is already taken");
    this.name = "UsernameTakenError";
  }
}

export class ReferralRedeemError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "ReferralRedeemError";
    this.code = code;
  }
}

// The delete route cancels Stripe subscriptions before soft-deleting, so it
// gets the same long timeout mobile uses for Stripe mutations.
const DELETE_ACCOUNT_TIMEOUT_MS = 60_000;

/**
 * Soft-deletes the signed-in account. The server cancels its subscriptions
 * and hands owned teams to their oldest admin/member (or deletes them).
 * Throws so the caller can show the server's message.
 */
export const deleteUserAccountOrThrow = async (userId: string): Promise<boolean> =>
  axiosInstance
    .post(`/user/${encodeURIComponent(userId)}/delete`, undefined, {
      timeout: DELETE_ACCOUNT_TIMEOUT_MS,
    })
    .then((res) => res.data);

/**
 * Admin-only "Wipe user data": permanently deletes the account's SQL records
 * and its Firebase login (media is retained), then waits for the Cloud Run
 * job to finish. Throws so the caller can show the server's message.
 */
export const wipeUserDataOrThrow = (userId: string, signal?: AbortSignal): Promise<boolean> =>
  startAndPollAccountDeletion(`/user/${encodeURIComponent(userId)}/records`, signal);

const accountDataAccess = (accessToken?: string) =>
  accessToken ? { headers: { "X-Account-Data-Access": accessToken } } : {};
export const fetchAccountDataStatus = async (accessToken?: string): Promise<AccountDataStatus> =>
  (await axiosInstance.get("/account-data/status", accountDataAccess(accessToken))).data;
export const sendAccountDataCode = async (email?: string, accessToken?: string): Promise<{ challengeId: string }> =>
  (await axiosInstance.post(email === undefined ? "/account-data/email/confirm" : "/account-data/email/send", email === undefined ? {} : { email }, accountDataAccess(accessToken))).data;
export const verifyAccountDataCode = async (challengeId: string, code: string): Promise<AccountDataVerification> =>
  (await axiosInstance.post("/account-data/email/verify", { challengeId, code })).data;
export const verifyAccountDataConfirmation = async (challengeId: string, code: string, accessToken?: string): Promise<{ confirmationToken: string }> =>
  (await axiosInstance.post("/account-data/email/confirm/verify", { challengeId, code }, accountDataAccess(accessToken))).data;
/**
 * Public, email-verified "Delete my account": the same soft delete as the
 * signed-in Delete account, plus removal of the login, done in one request.
 */
export const deleteEmailAccountData = async (
  accessToken: string,
  confirmationToken: string,
): Promise<{ deleted: boolean; firebaseDeleteFailed: boolean }> => {
  const { data } = await axiosInstance.delete("/account-data/records", {
    timeout: DELETE_ACCOUNT_TIMEOUT_MS,
    headers: {
      "X-Account-Data-Access": accessToken,
      "X-Deletion-Confirmation": confirmationToken,
    },
  });
  return {
    deleted: data?.status === "completed",
    firebaseDeleteFailed: data?.firebaseDeleteFailed === true,
  };
};

export const setUserDob = async (userId: string, dob: number): Promise<void> => {
  await axiosInstance.post("/user/dob", { userId, dob });
};

export const saveUserRealname = async (
  userId: string,
  firstName: string,
  lastName: string,
): Promise<void> => {
  await axiosInstance.post("/user/realname", { userId, firstName, lastName });
};

export const checkUsernameTaken = async (username: string): Promise<boolean> => {
  const res = await axiosInstance.get(
    `/user/check/username/${encodeURIComponent(username)}`,
  );
  return res.data === true;
};

export const saveUsername = async (userId: string, username: string): Promise<void> => {
  try {
    await axiosInstance.post("/user/username", { userId, username });
  } catch (err: any) {
    if (
      err?.response?.status === 400 &&
      String(errorDetail(err)).toLowerCase().includes("taken")
    ) {
      throw new UsernameTakenError();
    }
    throw err;
  }
};

export const sendParentPermissionEmail = async (
  userId: string,
  parentEmail: string,
): Promise<void> => {
  await axiosInstance.post("/user/sendParentPermissionEmail", { userId, parentEmail });
};

export const fetchParentPermissionStatus = async (): Promise<ParentPermissionStatus> => {
  const res = await axiosInstance.get("/user/parent-permission-status");
  const status = res.data?.status;
  return status === "approved" || status === "denied" ? status : "pending";
};

export const validateReferralCode = async (code: string): Promise<ReferralValidation> => {
  const res = await axiosInstance.get(
    `/referrals/validate/${encodeURIComponent(code.trim().toLowerCase())}`,
  );
  return { valid: !!res.data?.valid, username: res.data?.username, profilePicture: res.data?.profilePicture };
};

export const redeemReferralCode = async (code: string): Promise<void> => {
  try {
    await axiosInstance.post(
      `/referrals/${encodeURIComponent(code.trim().toLowerCase())}/redeem`,
      {},
    );
  } catch (err: any) {
    const detail = errorDetail(err) as any;
    if (detail && typeof detail === "object" && detail.code) {
      throw new ReferralRedeemError(detail.code, detail.message ?? detail.code);
    }
    throw err;
  }
};

export const saveUserProfilePicture = async (
  userId: string,
  profilePicture: string,
): Promise<void> => {
  await axiosInstance.post("/user/picture", {
    userId,
    userIdToUpdate: userId,
    profilePicture,
  });
};

export const updateUserPlayerProfile = async (
  userId: string,
  patch: { position?: string | null; heightCm?: number | null },
): Promise<void> => {
  await axiosInstance.post("/user/update", { userId, ...patch });
};

export const markTutorialSeen = async (
  userId: string,
  tutorialId: TourId,
): Promise<void> => {
  await axiosInstance.post("/user/tutorialSeen", { userId, tutorialId });
};

const PROTECTED_WORDS_PAGE = 500;
const PROTECTED_WORDS_MAX_PAGES = 20;

export const fetchProtectedWordList = async (): Promise<string[]> => {
  const words: string[] = [];
  for (let page = 0; page < PROTECTED_WORDS_MAX_PAGES; page++) {
    const res = await axiosInstance.get("/protected-words", {
      params: { page, limit: PROTECTED_WORDS_PAGE },
    });
    const items: { word?: string }[] = res.data?.items ?? [];
    items.forEach((item) => item.word && words.push(item.word));
    if (items.length < PROTECTED_WORDS_PAGE) break;
  }
  return words;
};

export const updateUserRealname = async (
  userId: string,
  firstName?: string,
  lastName?: string,
) => {
  return axiosInstance
    .post("/user/realname", { userId, firstName, lastName })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateUserRealname`:", err));
};

export const updateUserDescription = async (
  userId: string,
  description?: string,
) => {
  return axiosInstance
    .post("/user/description", { userId, description })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateUserDescription`:", err));
};

export const updateUserSocials = async (
  userId: string,
  externalLink?: string,
  instagramProfile?: string,
  youtubeProfile?: string,
  tiktokProfile?: string,
) => {
  return axiosInstance
    .post("/user/socials", {
      userId,
      externalLink,
      instagramProfile,
      youtubeProfile,
      tiktokProfile,
    })
    .then((res) => res.data)
    .catch((err) => console.error("Error in `updateUserSocials`:", err));
};

// === Shot Favorites ===

export const fetchSessionFavoriteShotIds = async (
  sessionId: string,
  userId: string,
): Promise<string[]> => {
  return axiosInstance
    .get(`/session/v4/shots/favorites/${sessionId}/${userId}`)
    .then((res) => res.data as string[])
    .catch((err) => {
      console.error("Error in `fetchSessionFavoriteShotIds`:", err);
      throw err;
    });
};

export const favoriteShot = async (
  userId: string,
  shotId: string,
): Promise<unknown> => {
  return axiosInstance
    .post("/session/v4/shot/favorite", { userId, shotId })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error in `favoriteShot`:", err);
      throw err;
    });
};

export const unfavoriteShot = async (
  userId: string,
  shotId: string,
): Promise<boolean> => {
  return axiosInstance
    .post("/session/v4/shot/unfavorite", { userId, shotId })
    .then((res) => res.data as boolean)
    .catch((err) => {
      console.error("Error in `unfavoriteShot`:", err);
      throw err;
    });
};

// === Hour Credits (purchased processing hours) Endpoints ===================

export const fetchApiPricingCodes = async (): Promise<ApiPricingCode[]> =>
  axiosInstance.get("/admin/api-pricing-codes").then((res) => res.data);

export const createApiPricingCode = async (body: {
  code: string;
  rateCentsPerHour: number;
  maxRedemptions?: number | null;
  expiresAt?: string | null;
  note?: string | null;
}): Promise<ApiPricingCode> =>
  axiosInstance.post("/admin/api-pricing-codes", body).then((res) => res.data);

export const updateApiPricingCode = async (
  id: number,
  body: Partial<Pick<ApiPricingCode, "maxRedemptions" | "expiresAt" | "active" | "note">>,
): Promise<ApiPricingCode> =>
  axiosInstance.patch(`/admin/api-pricing-codes/${id}`, body).then((res) => res.data);

export const fetchBillingConfig = async (): Promise<ExtraHoursConfig> =>
  axiosInstance
    .get("/admin/billing/config")
    .then((res) => res.data as ExtraHoursConfig);

export const updateBillingConfig = async (
  payload: Partial<ExtraHoursConfig>
): Promise<ExtraHoursConfig> =>
  axiosInstance
    .put("/admin/billing/config", payload)
    .then((res) => res.data as ExtraHoursConfig)
    .catch((err) => {
      console.error("Error in `updateBillingConfig`:", err);
      throw err;
    });

export const fetchRegionalPricing = async (): Promise<RegionalPricingConfig> =>
  axiosInstance
    .get("/admin/billing/regional-pricing")
    .then((res) => res.data as RegionalPricingConfig);

export const updateRegionalPricing = async (
  rows: RegionalPrice[]
): Promise<RegionalPricingConfig> =>
  axiosInstance
    .put("/admin/billing/regional-pricing", { rows })
    .then((res) => res.data as RegionalPricingConfig)
    .catch((err) => {
      console.error("Error in `updateRegionalPricing`:", err);
      throw err;
    });

export const fetchHourCredits = async (
  page: number = 1,
  pageSize: number = 25,
  filters: HourCreditFilters = {}
): Promise<HourCreditPage> => {
  // Query params are snake_case server-side.
  const params: Record<string, string | number> = { page, page_size: pageSize };
  if (filters.groupId) params.group_id = filters.groupId;
  if (filters.source?.trim()) params.source = filters.source.trim();
  if (filters.pricingTier) params.tier = filters.pricingTier;
  if (filters.dateFrom)
    params.date_from = Math.floor(new Date(filters.dateFrom).getTime() / 1000);
  if (filters.dateTo)
    params.date_to = Math.floor(new Date(filters.dateTo).getTime() / 1000);

  return axiosInstance
    .get("/admin/billing/hour-credits", { params })
    .then((res) => res.data as HourCreditPage)
    .catch((err) => {
      console.error("Error in `fetchHourCredits`:", err);
      // Reads degrade to an empty page rather than blanking the screen.
      return {
        items: [],
        page,
        pageSize,
        totalCount: 0,
        totalPages: 0,
        hasMore: false,
      };
    });
};

export const fetchGroupHourCredits = async (
  groupId: number,
  includeExpired: boolean = false
): Promise<GroupHourCredits> =>
  axiosInstance
    .get(`/admin/billing/groups/${groupId}/hour-credits`, {
      params: { include_expired: includeExpired },
    })
    .then((res) => res.data as GroupHourCredits)
    .catch((err) => {
      console.error("Error in `fetchGroupHourCredits`:", err);
      throw err;
    });

export const grantHourCredits = async (
  groupId: number,
  hours: number,
  note?: string
): Promise<GroupHourCredits> =>
  axiosInstance
    .post(`/admin/billing/groups/${groupId}/hour-credits`, { hours, note })
    .then((res) => res.data as GroupHourCredits)
    .catch((err) => {
      console.error("Error in `grantHourCredits`:", err);
      throw err;
    });

export const fetchOrganizationOwnerInvitation = async (organizationId: string): Promise<{ canResend: boolean }> => {
  const response = await axiosInstance.get(`/admin/organizations/${organizationId}/owner-invitation`);
  return response.data;
};

export const resendOrganizationOwnerInvitation = async (organizationId: string): Promise<{ emailSent: boolean }> => {
  const response = await axiosInstance.post(`/admin/organizations/${organizationId}/owner-invitation/resend`);
  return response.data;
};

export type SupportProblemArea =
  | "upload"
  | "processing"
  | "video"
  | "teams"
  | "billing"
  | "other";

export type SupportCaseInput = {
  problemArea: SupportProblemArea;
  subject: string;
  description: string;
  critical: boolean;
  sessionId?: string;
  files: File[];
};

/** Files a Hub support case: emailed to support (with files) and posted to Slack. */
export const submitSupportCase = async (input: SupportCaseInput): Promise<void> => {
  const form = new FormData();
  form.append("problem_area", input.problemArea);
  form.append("subject", input.subject);
  form.append("description", input.description);
  form.append("critical", String(input.critical));
  if (input.sessionId) form.append("session_id", input.sessionId);
  input.files.forEach((file) => form.append("files", file, file.name));
  await axiosInstance.post("/support/case", form);
};
